import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import type { Kopf } from '../komponenten/Rahmen';
import { Abschnitt, Aktenrumpf, Feldgruppe, Kennzahlenleiste, Reiterleiste } from '../komponenten/Akte';
import { Aktenverweis, Verbunden, type Aktenwege } from '../komponenten/Aktenwege';
import { KundeVerweis } from '../komponenten/Kontaktkarte';
import { Maskenverweis } from '../komponenten/Maskenverweis';
import { markenklasse } from '../marken';
import { STATUSMARKE } from './Angebotsliste';

import {
  ARTNAME,
  STATUSNAME,
  WERTUNGSNAME,
  type Ablehnungsgrund,
  type Angebot,
  type Angebotsposition,
  type Angebotsstatus,
  type Positionsart,
  type Wertung,
  ablehnungsgruende as holeGruende,
  akte as holeAkte,
  feldSichern,
  loeschen,
  neueVersion,
  neuOrdnen,
  positionAnlegen,
  positionLoeschen,
  statusSetzen,
  VERTRAGSARTNAME,
} from '../api/angebote';
import { ausAngebot } from '../api/auftraege';
import { Belegabschnitt } from '../beleg/Belegabschnitt';
import {
  VERTRIEBSFORMNAME,
  preisauskunft,
  type Artikelzeile,
  type Preisauskunft,
  type Vertriebsform,
} from '../api/produkte';
import { Artikelwahl } from '../produkt/Artikelwahl';
import {
  buchbare,
  exklusive,
  standorte,
  type Exklusivtermin,
  type Terminauswahl,
} from '../api/termine';
import { terminbezug } from '../api/terminname';
import { ApiFehler } from '../api/fehler';
import { ERSTE_RASTERZEIT, RASTERZEITEN } from '../komponenten/Beginnwahl';
import { Meldung } from '../komponenten/Meldung';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { AbTablet } from '../komponenten/AbTablet';
import { Gutscheinvormerkungen } from '../rechnung/BgZuschuss';
import { useTelefon } from '../komponenten/telefon';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { EntwurfLoeschen } from '../komponenten/EntwurfLoeschen';
import { Rundungshinweis } from '../komponenten/Rundungshinweis';
import { alsEuro, alsMengenangabe, alsProzent } from '../betrag';
import { Terminoptionen } from './Terminoptionen';
import { zumAngebot, type Optionsanfrage } from '../api/optionsanfragen';

/** Ein Tag in deutscher Schreibweise — ISO oder Zeitpunkt (FRS-01). */
function alsTag(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });
}

type Reiter = 'positionen' | 'kopf' | 'terminoptionen' | 'beleg';

/**
 * Die Steuerschluessel, die eine Rabattzeile tragen kann.
 *
 * Als kurze Liste in der Maske und NICHT aus fach.steuerschluessel geladen:
 * Der Abzug muss zu einer Satzgruppe passen, die im Beleg vorkommt, und das
 * sind in der Praxis diese drei. Wer einen weiteren braucht, bekommt eine
 * Auswahl aus dem Bestand — dann aber mit dem Zeitraum, den PRD-16 an jedem
 * Schluessel fuehrt.
 */
const SATZWAHL: readonly string[] = ['DE-19', 'DE-7', 'DE-0'];

const REITER: readonly { kennung: Reiter; beschriftung: string }[] = [
  { kennung: 'positionen', beschriftung: 'Positionen' },
  { kennung: 'kopf', beschriftung: 'Kopf und Texte' },
  /*
   * ANG-30: Der Terminvorgang gehoert AN DAS ANGEBOT und nicht daneben —
   * „aus beiden Richtungen auffindbar". Von hier fragt der Vertrieb; die
   * Antwort der Disposition steht an derselben Stelle.
   */
  { kennung: 'terminoptionen', beschriftung: 'Terminoptionen' },
  { kennung: 'beleg', beschriftung: 'Beleg' },
  /*
   * SUMMEN UND VERSIONEN SIND KEINE REITER MEHR (ÄA-41, Auftrag #18): Sie
   * stehen in der Seitenleiste neben den Positionen. Wer eine Zeile ändert,
   * sieht die Summe sich ändern, ohne den Reiter zu wechseln.
   */
];

/**
 * Die Angebotsakte (Lastenheft Kapitel 6).
 *
 * DREI DINGE ENTSCHEIDET DIESE MASKE NICHT, und sie tut auch nicht so:
 *
 *   * Die BETRAEGE. Sie kommen vom Server, gerechnet aus den eingefrorenen
 *     Eingaben. Eine Zwischensumme im Browser waere die zweite Wahrheit
 *     neben dem Rechenkern.
 *   * Die zulaessigen STATUSWECHSEL. Sie stehen in `moeglicheStatus` und
 *     kommen aus der Kantenliste der Datenbank (ANG-16). Eine Liste im
 *     Browser liefe beim ersten neuen Zustand auseinander.
 *   * Ob eine Fassung AENDERBAR ist. `eingefroren` sagt es; die Maske blendet
 *     dann die Handlungen aus, statt sie anzubieten und scheitern zu lassen.
 */
/** "2026-09-30" wird zu "30.09.2026" — die Unterzeile ist Fliesstext. */
function tagDeutsch(iso: string): string {
  const [jahr, monat, tag] = iso.split('-');
  return tag === undefined ? iso : `${tag}.${monat}.${jahr}`;
}

/**
 * Ein Zeitpunkt in ORTSZEIT — „12.11.2026, 09:15" (FRS-01, LOG-03).
 *
 * Der Termin kommt als UTC über die Schnittstelle; gemeint ist die Uhr am
 * Standort. Ein Kunde, der um 9 Uhr da sein soll, liest sonst im Winter 8.
 */
function zeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

/**
 * Ein gefundener Preis als PLATZHALTER im Eingabefeld — „158.00", nicht
 * „158,00 €" und nicht „158.0000".
 *
 * Der Platzhalter sagt zweierlei zugleich: WAS der Preis ist und WIE das Feld
 * ihn erwartet. Ein deutsch gesetzter Betrag laedt ein, „158,00 €" zu tippen,
 * und das weist der Server ab; die vier Nachkommastellen der Ablage (RND-01)
 * wiederum liest niemand als Preis. Zwei bleiben stehen, mehr nur, wenn sie
 * etwas tragen.
 */
function alsTippbar(wert: string | null | undefined): string | undefined {
  if (wert === null || wert === undefined || wert === '') return undefined;
  const [ganz = '', nach = ''] = wert.split('.');
  const gekuerzt = nach.replace(/0+$/, '').padEnd(2, '0');
  return `${ganz}.${gekuerzt}`;
}

/** Nur der Tag — für Fristen, die keine Uhrzeit tragen. */
function nurTag(iso: string): string {
  return iso.length === 10
    ? tagDeutsch(iso)
    : new Date(iso).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Europe/Berlin',
      });
}

export function Angebotsakte({
  id,
  darfAendern,
  darfVersenden,
  darfAuftragAnlegen,
  darfLoeschen = false,
  bgVormerkungen,
  beiKopf,
  beiListe,
  beiVersion,
  beiAuftrag,
  wege,
  beiOptionsanfragen,
}: {
  readonly id: string;
  readonly darfAendern: boolean;
  readonly darfVersenden: boolean;
  /** `angebot/loeschen` (0033): den Entwurf logisch löschen (DAT-25) — Auftrag CRM-119. */
  readonly darfLoeschen?: boolean;
  /**
   * AeA-71: vorgemerkte Gutscheine ohne Namen — nur mit `bgzuschuss/sehen`;
   * vormerken verlangt zusaetzlich `bgzuschuss/erfassen` und `angebot/aendern`.
   */
  readonly bgVormerkungen?: { readonly darfVormerken: boolean };
  /** CRM-118 (AUF-06): die Wege zu den verbundenen Akten, nach Recht gesetzt. */
  readonly wege?: Aktenwege;
  /** CRM-118: zu den Anfragen der Disposition — nur mit `terminoption_freigeben` (ANG-38). */
  readonly beiOptionsanfragen?: () => void;
  /** ANG-18: die Umwandlung haengt an `auftrag/anlegen`, nicht an `angebot/*`. */
  readonly darfAuftragAnlegen: boolean;
  readonly beiKopf: (kopf: Kopf) => void;
  readonly beiListe: () => void;
  readonly beiVersion: (id: string) => void;
  readonly beiAuftrag: (id: string) => void;
}): JSX.Element {
  const [angebot, setzeAngebot] = useState<Angebot | undefined>(undefined);
  const [reiter, setzeReiter] = useState<Reiter>('positionen');
  const telefon = useTelefon();
  const [gruende, setzeGruende] = useState<readonly Ablehnungsgrund[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  /*
   * DIE MELDUNG STEHT AM SEITENANFANG, DIE HANDLUNG WEIT DARUNTER.
   *
   * Wer unten auf "Hinzufuegen" drueckt, sieht oben eine Meldung, die er
   * nicht sieht. Gemessen am 31.08.2026: Der Anwender berichtete die
   * Fehlerkennung aus der letzten Zeile des Kastens — den Satz darueber,
   * der sagt, was zu tun ist, hatte er nicht gelesen. ERR-01 verlangt den
   * dritten Teil; ein dritter Teil ausserhalb des Blickfelds ist keiner.
   */
  const meldungsverweis = useRef<HTMLDivElement>(null);
  /* Im Ref: Der Kopf wird in `laden` gesetzt, und `laden` soll nicht bei jedem Bild neu entstehen. */
  const zurListe = useRef(beiListe);
  zurListe.current = beiListe;
  const wegeImKopf = useRef(wege);
  wegeImKopf.current = wege;

  const laden = useCallback(async (): Promise<void> => {
    try {
      const geladen = await holeAkte(id);
      setzeAngebot(geladen);
      /*
       * Die Aufteilung der Vorlage (Skizze zu M-31: "Angebot 2026-AN-00327 ·
       * Entwurf" / "Angebot bearbeiten" / "Autohaus Reimers GmbH · ... ·
       * gültig bis 30.09.2026"). Der Titel traegt den BETREFF — er ist das,
       * was der Kunde als Ueberschrift des Belegs liest.
       */
      /*
       * DER AKTENKOPF (ÄA-41): Sinnbild, „Angebot · Nummer", der Betreff als
       * Titel, der Status als Marke daneben; darunter die Kennzahlen. Die
       * Frist steht in Rost, solange das Angebot beim Kunden liegt.
       */
      const laeuft = geladen.status === 'versendet' || geladen.status === 'verhandlung';
      beiKopf({
        kicker: `Angebot · ${geladen.angebotsnummer}`,
        titel: geladen.betreff,
        unterzeile: `${geladen.kundenname} · gültig bis ${tagDeutsch(geladen.gueltigBis)}`,
        sinnbild: 'file-text',
        kennzeichen: (
          <>
            <span className={markenklasse(STATUSMARKE[geladen.status])}>
              {STATUSNAME[geladen.status]}
            </span>
            {geladen.version > 1 && <span className="marke">Version {geladen.version}</span>}
            {geladen.eingefroren && <span className="marke marke--gesperrt">eingefroren</span>}
          </>
        ),
        kennzahlen: (
          <Kennzahlenleiste
            zahlen={[
              {
                beschriftung: 'Kunde',
                wert: (
                  <Aktenverweis ziel="kunde" id={geladen.kunde} wege={wegeImKopf.current}>
                    {geladen.kundenname}
                  </Aktenverweis>
                ),
              },
              {
                beschriftung: 'Ansprechpartner',
                // CRM-181: die Kontaktkarte am Ansprechpartner, am Recht der Kundenakte.
                wert:
                  geladen.ansprechpartnerName === null ? (
                    '—'
                  ) : (
                    <KundeVerweis
                      kundeId={geladen.kunde}
                      ansprechpartnerId={geladen.ansprechpartner}
                      zurAkte={wegeImKopf.current?.kunde}
                    >
                      {geladen.ansprechpartnerName}
                    </KundeVerweis>
                  ),
              },
              { beschriftung: 'Belegdatum', wert: tagDeutsch(geladen.belegdatum) },
              { beschriftung: 'Gültig bis', wert: tagDeutsch(geladen.gueltigBis), frist: laeuft },
              { beschriftung: 'Brutto', wert: alsEuro(geladen.summen.belegbrutto) },
              { beschriftung: 'Bearbeitung', wert: geladen.bearbeiterName ?? '—' },
            ]}
          />
        ),
        handlungen: (
          <button type="button" className="knopf" onClick={() => zurListe.current()}>
            Zur Liste
          </button>
        ),
      });
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }, [id, beiKopf]);

  useEffect(() => {
    void laden();
  }, [laden]);

  useEffect(() => {
    void holeGruende().then(setzeGruende).catch(() => setzeGruende([]));
  }, []);

  async function handle(was: () => Promise<unknown>, meldung: string): Promise<void> {
    await handleMit(async () => {
      await was();
      return meldung;
    });
  }

  /**
   * Wie `handle`, nur bestimmt der Vorgang SELBST, was er meldet.
   *
   * Seit dem 16.09.2026 kann eine Änderung die Fassung wechseln (ANG-15: „Jede
   * Änderung NACH DEM VERSAND erzeugt eine neue Version."). „Gesichert." wäre
   * dann die halbe Wahrheit — der Anwender steht danach auf einer anderen
   * Fassung, und das muss dastehen.
   */
  async function handleMit(was: () => Promise<string>): Promise<void> {
    try {
      await bestaetigt(was);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
      // Erst nach dem Zeichnen — vorher gibt es den Kasten noch nicht.
      window.setTimeout(() => meldungsverweis.current?.focus(), 0);
    }
  }

  /**
   * Wie `handleMit`, nur WIRFT ein Fehler weiter: Die Rückfrage (Auftrag
   * CRM-103) zeigt ihn in ihrem Pop-up und bleibt offen. Schließen muss sie
   * der Aufrufer, wenn das Versprechen erfüllt ist.
   */
  async function bestaetigt(was: () => Promise<string>): Promise<void> {
    const meldung = await was();
    setzeRueckmeldung(meldung);
    setzeFehler(undefined);
    await laden();
  }

  /**
   * ANG-15: Hat der Server eine neue Fassung angelegt, wechselt die Maske
   * dorthin — und sagt es. Die alte bleibt unverändert einsehbar.
   */
  function nachFassung(
    antwort: { readonly angebot?: { readonly id: string; readonly version: number } },
    meldung: string,
  ): string {
    if (antwort.angebot === undefined) return meldung;
    beiVersion(antwort.angebot.id);
    return `${meldung} Fassung ${String(antwort.angebot.version)} angelegt — die versendete `
      + 'bleibt unverändert einsehbar.';
  }

  if (angebot === undefined) {
    return (
      <>
        {/* Nach einem Fehler lädt nichts mehr — dann steht nur die Meldung da. */}
        {fehler !== undefined ? <Meldung fehler={fehler} /> : <p role="status">Wird geladen …</p>}
      </>
    );
  }

  // ANG-15: Eine eingefrorene Fassung wird angesehen, nicht bearbeitet.
  // ANG-18, AUF-14: ebenso ein Angebot, aus dem ein Auftrag entstanden ist —
  // Aenderungen gehoeren dann in den Auftrag (Auftraggeber, 27.09.2026).
  const bearbeitbar = darfAendern && !angebot.eingefroren && angebot.auftrag === null;

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} verweis={meldungsverweis} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      {angebot.eingefroren && (
        <p className="warnung">
          Diese Fassung ist eingefroren und bleibt unverändert einsehbar. Änderungen
          gehören in die aktuelle Fassung — sie steht unter „Fassungen“ in der Seitenleiste.
        </p>
      )}

      {angebot.auftrag !== null && (
        <p className="warnung">
          Aus diesem Angebot ist der Auftrag {angebot.auftrag.auftragsnummer} entstanden.
          Das Angebot bleibt unverändert als Nachweis dessen, was der Kunde angenommen hat;
          Änderungen gehören in den Auftrag.{' '}
          <button
            type="button"
            className="knopf knopf--klein"
            onClick={() => beiAuftrag(angebot.auftrag!.id)}
          >
            Auftrag öffnen
          </button>
        </p>
      )}

      <Aktenrumpf
        haupt={
          <>
            <Reiterleiste
              beschriftung="Angebot"
              gewaehlt={reiter}
              beiWahl={setzeReiter}
              reiter={REITER.map((r) => ({
                ...r,
                ...(r.kennung === 'positionen' ? { zahl: angebot.positionen.length } : {}),
              }))}
            />
            <div role="tabpanel" id={`tafel-${reiter}`} aria-labelledby={`reiter-${reiter}`}>
              {/* Positionen bearbeiten ab Tablet (Auftrag CRM-106, ÄA-63); auf dem Telefon nur lesen. */}
              {reiter === 'positionen' && (
                <AbTablet an={bearbeitbar} teil>
                  <Positionen
                    angebot={angebot}
                    bearbeitbar={bearbeitbar && !telefon}
                    beiAnlegen={(eingabe) =>
                      void handleMit(async () =>
                        nachFassung(await positionAnlegen(angebot.id, eingabe), 'Position hinzugefügt.'),
                      )
                    }
                    beiLoeschen={(position) =>
                      bestaetigt(async () =>
                        nachFassung(await positionLoeschen(angebot.id, position), 'Position entfernt.'),
                      )
                    }
                    beiOrdnen={(reihenfolge) =>
                      void handleMit(async () =>
                        nachFassung(await neuOrdnen(angebot.id, reihenfolge), 'Reihenfolge geändert.'),
                      )
                    }
                  />
                </AbTablet>
              )}

              {reiter === 'positionen' && bgVormerkungen !== undefined && (
                <Gutscheinvormerkungen
                  bezug={{
                    art: 'angebot',
                    id: angebot.id,
                    positionen: angebot.positionen
                      .filter((p) => p.art === 'artikel' || p.art === 'freitext')
                      .map((p) => ({ id: p.id, text: `${p.reihenfolge}. ${p.bezeichnung}` })),
                  }}
                  // AeA-71: Eine angenommene, abgelehnte oder abgelaufene Fassung bleibt gesperrt (ANG-15).
                  darfVormerken={
                    bgVormerkungen.darfVormerken
                    && !angebot.eingefroren
                    && angebot.auftrag === null
                    && !['angenommen', 'abgelehnt', 'abgelaufen'].includes(angebot.status)
                    && !telefon
                  }
                />
              )}

              {reiter === 'kopf' && (
                <Kopfdaten
                  angebot={angebot}
                  zurKunde={wege?.kunde}
                  bearbeitbar={bearbeitbar}
                  beiSichern={(feld, wert) =>
                    void handleMit(async () =>
                      nachFassung(
                        await feldSichern(angebot.id, feld, wert, angebot.stand),
                        'Gesichert.',
                      ),
                    )
                  }
                />
              )}

              {reiter === 'terminoptionen' && <Terminoptionen angebot={angebot.id} />}

              {reiter === 'beleg' && (
                <Belegabschnitt
                  belegart="angebot"
                  beleg={angebot.id}
                  /*
                    ANG-12: Ein erzeugtes PDF ist der Beleg, den der Kunde bekommt —
                    wer ihn druckt, sagt einen Preis zu. Das haengt deshalb an
                    `angebot/versenden` und nicht an `angebot/aendern`.
                  */
                  darfErzeugen={darfVersenden}
                />
              )}
            </div>
          </>
        }
        seite={
          <>
            <div className="akte__seitentitel">
              <span>Status und Summen</span>
            </div>
            <Statusleiste
              angebot={angebot}
              gruende={gruende}
              darfVersenden={darfVersenden}
              darfAendern={darfAendern}
              darfAuftragAnlegen={darfAuftragAnlegen}
              {...(beiOptionsanfragen === undefined ? {} : { beiOptionsanfragen })}
              beiUebernahme={(positionen) =>
                void handle(async () => {
                  const neu = await ausAngebot({ angebot: angebot.id, positionen });
                  beiAuftrag(neu.id);
                }, 'Auftrag angelegt.')
              }
              beiStatus={(status, grund, terminwahl) =>
                void handle(
                  () => statusSetzen(angebot.id, status, grund, terminwahl),
                  `Status auf „${STATUSNAME[status]}“ gesetzt.`,
                )
              }
              beiStatusBestaetigt={(status, grund) =>
                bestaetigt(async () => {
                  await statusSetzen(angebot.id, status, grund);
                  return `Status auf „${STATUSNAME[status]}“ gesetzt.`;
                })
              }
              beiNeueVersion={() =>
                void handle(async () => {
                  const neu = await neueVersion(angebot.id);
                  beiVersion(neu.id);
                }, 'Neue Version angelegt.')
              }
            />
            {/*
              * DAT-25, ANG-15: nur der Entwurf in erster Fassung. Was der Kunde
              * je in der Hand hatte, bleibt stehen — dafür gibt es „abgelehnt“.
              */}
            {darfLoeschen && angebot.status === 'entwurf' && angebot.version === 1
              && !angebot.eingefroren && (
              <EntwurfLoeschen
                frage="Angebotsentwurf löschen?"
                unterzeile={`${angebot.angebotsnummer} · ${angebot.kundenname}`}
                gesperrt={
                  // ANG-36: die offene Terminoption hält das Angebot.
                  angebot.positionen.some((p) => p.optionsstand === 'offen')
                    ? 'Zu löschen erst, wenn die Disposition die Terminoption beantwortet hat.'
                    : undefined
                }
                beiLoeschen={async () => {
                  await loeschen(angebot.id);
                  beiListe();
                }}
              >
                {/* SYS-16, KAP-10: Papierkorb; gehaltene Plätze werden frei. */}
                <p>
                  Das Angebot {angebot.angebotsnummer} verschwindet aus der Liste und liegt 30 Tage
                  im Papierkorb. Vorgemerkte Plätze und Optionen gibt es sofort frei — sie kehren
                  beim Wiederherstellen nicht zurück.
                </p>
              </EntwurfLoeschen>
            )}
            <Summen angebot={angebot} />
            <Angebotsverbunden angebot={angebot} wege={wege} />
            <Versionen angebot={angebot} beiVersion={beiVersion} />
          </>
        }
      />
    </>
  );
}

/**
 * DIE FASSUNGEN DES VORGANGS (ANG-15) — in der Seitenleiste, neben den
 * Positionen der gezeigten Fassung.
 */
function Versionen({
  angebot,
  beiVersion,
}: {
  readonly angebot: Angebot;
  readonly beiVersion: (id: string) => void;
}): JSX.Element {
  return (
    <section className="seitenblock" aria-labelledby="angebot-fassungen">
      <h2 id="angebot-fassungen" className="seitenblock__titel">
        Fassungen
      </h2>
      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle">
          <thead>
            <tr>
              <th scope="col">Version</th>
              <th scope="col">Status</th>
              <th scope="col">angelegt</th>
              <th scope="col">Zustand</th>
            </tr>
          </thead>
          <tbody>
            {angebot.versionen.map((v) => (
              <tr key={v.id} className={v.id === angebot.id ? 'liste__zeile--gewaehlt' : undefined}>
                <td>
                  <button type="button" className="liste__oeffnen" onClick={() => beiVersion(v.id)}>
                    Version {v.version}
                  </button>
                </td>
                <td>{STATUSNAME[v.status]}</td>
                <td>{tagDeutsch(v.angelegtAm.slice(0, 10))}</td>
                <td>
                  {v.eingefroren ? (
                    <span className="marke marke--gesperrt">eingefroren</span>
                  ) : (
                    <span className="marke">in Arbeit</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* ANG-07 */}
      <p className="feld__hilfe">
        Alle Fassungen tragen dieselbe Angebotsnummer {angebot.angebotsnummer} — es ist ein
        Vorgang und nicht mehrere.
      </p>
    </section>
  );
}

/**
 * Der Statusfluss als Leiste (ANG-16).
 *
 * DIE KNOEPFE ENTSTEHEN AUS `moeglicheStatus` und nicht aus einer Liste im
 * Browser. Die Kanten stehen in `fach.angebotsuebergang`; eine zweite Liste
 * hier liefe beim ersten neuen Zustand auseinander, und der Anwender bekaeme
 * einen Knopf, der zuverlaessig scheitert.
 */
function Statusleiste({
  angebot,
  gruende,
  darfVersenden,
  darfAendern,
  darfAuftragAnlegen,
  beiStatus,
  beiStatusBestaetigt,
  beiNeueVersion,
  beiUebernahme,
  beiOptionsanfragen,
}: {
  readonly angebot: Angebot;
  readonly gruende: readonly Ablehnungsgrund[];
  readonly beiOptionsanfragen?: () => void;
  readonly darfVersenden: boolean;
  readonly darfAendern: boolean;
  readonly darfAuftragAnlegen: boolean;
  readonly beiUebernahme: (positionen: readonly string[]) => void;
  readonly beiStatus: (
    status: Angebotsstatus,
    grund: { ablehnungsgrund?: string | null; ablehnungstext?: string | null },
    terminwahl?: readonly { readonly anfrage: string; readonly alternative: string }[],
  ) => void;
  /** Wie `beiStatus`, nur wirft ein Fehler — für den Weg über die Rückfrage (CRM-103). */
  readonly beiStatusBestaetigt: (
    status: Angebotsstatus,
    grund: { ablehnungsgrund?: string | null; ablehnungstext?: string | null },
  ) => Promise<void>;
  readonly beiNeueVersion: () => void;
}): JSX.Element {
  const [ablehnung, setzeAblehnung] = useState(false);
  /* Auftrag CRM-103: welche Rückfrage gerade offen ist. */
  const [rueckfrage, setzeRueckfrage] = useState<'versenden' | 'ablehnung' | null>(null);
  /* ANG-36: Wie viele Zeilen noch auf die Zusage der Disposition warten. */
  const offeneTermine = angebot.positionen.filter((p) => p.optionsstand === 'offen').length;
  /* ANG-39: die Anfragen, bei denen die Annahme zu wählen hat. */
  const [zuWaehlen, setzeZuWaehlen] = useState<readonly Optionsanfrage[]>([]);
  const [terminwahl, setzeTerminwahl] = useState<Readonly<Record<string, string>>>({});
  const [grund, setzeGrund] = useState('');
  const [text, setzeText] = useState('');
  const [uebernahme, setzeUebernahme] = useState(false);
  const [gewaehlt, setzeGewaehlt] = useState<readonly string[]>([]);

  const wechselbar = !angebot.eingefroren && darfVersenden;
  /*
   * ANG-18: umgewandelt wird das ANGENOMMENE Angebot. Der Knopf erscheint
   * deshalb erst dann — vorher waere er ein Angebot, das der Server abweist,
   * und der Anwender lernte die Regel aus einer Fehlermeldung statt aus der
   * Maske.
   */
  const uebernehmbar =
    angebot.status === 'angenommen' && darfAuftragAnlegen && angebot.auftrag === null;
  /** Nur wirksame Zeilen sind waehlbar: Alternativen sind das Abgewaehlte. */
  const waehlbar = angebot.positionen.filter((p) => p.wertung === 'wirksam');

  /**
   * DIE ANNAHME FRAGT, WO ES ETWAS ZU WÄHLEN GIBT (ANG-39, ANG-31).
   *
   * Auftraggeber, 14.09.2026: „Den weg der Auswahl durch den vertrieb bei der
   * Annahme."
   *
   * Hat die Disposition zu einer Anfrage MEHRERE Termine zugesagt, wird
   * gefragt, welchen der Kunde genommen hat — die übrigen geben ihre Plätze
   * sofort frei und ihre Kalendereinträge werden abgesagt. Ist nur einer
   * zugesagt, geht die Annahme ohne Rückfrage durch: Dann gibt es nichts zu
   * wählen.
   *
   * GEFRAGT WIRD ERST BEIM KLICK und nicht beim Öffnen der Akte: Die Auskunft
   * kostet eine Abfrage, und gebraucht wird sie nur in diesem Augenblick.
   */
  async function annahmeVorbereiten(): Promise<void> {
    const anfragen = await zumAngebot(angebot.id);
    const offen = anfragen.filter(
      (a) =>
        a.gewaehlteAlternative === null
        && a.alternativen.filter((alt) => alt.antwort === 'zugesagt').length > 1,
    );
    if (offen.length === 0) {
      beiStatus('angenommen', {});
      return;
    }
    setzeTerminwahl(
      Object.fromEntries(
        offen.map((a) => [
          a.id,
          a.alternativen.find((alt) => alt.antwort === 'zugesagt')!.id,
        ]),
      ),
    );
    setzeZuWaehlen(offen);
  }

  return (
    <section className="seitenblock" aria-labelledby="angebot-status">
      <div className="seitenblock__kopf">
        <h2 id="angebot-status" className="seitenblock__titel">
          Status
        </h2>
        <span className={markenklasse(STATUSMARKE[angebot.status])}>{STATUSNAME[angebot.status]}</span>
      </div>

      {angebot.status === 'abgelehnt' && (
        <p className="hinweiszeile">
          Abgelehnt:{' '}
          {gruende.find((g) => g.schluessel === angebot.ablehnungsgrund)?.bezeichnung
            ?? angebot.ablehnungsgrund}
          {angebot.ablehnungstext !== null && ` — ${angebot.ablehnungstext}`}
        </p>
      )}

      {/*
        DER HINWEIS VOR DEM VERSAND (ANG-36).

        Auftraggeber, 13.09.2026: „in der angebotsmaske sollen die termine so
        lange auf ‚auf anfrage' stehen, bis die orga sie geprüft hat, das soll
        auch als hinweis beim versenden zu sehen sein."

        Er steht NEBEN dem Knopf und nicht hinter ihm: Ein Hinweis, der erst
        nach dem Klick erscheint, kommt zu spät — das Angebot ist dann beim
        Kunden. Er hält den Versand auch nicht auf: ANG-36 verlangt die
        Kennzeichnung, nicht die Zusage. „Termin auf Anfrage" ist eine
        zulässige Aussage gegenüber dem Kunden, und oft die einzige ehrliche.
      */}
      {offeneTermine > 0 && (
        <p className="warnung">
          {offeneTermine === 1
            ? 'Eine Position wartet noch auf die Disposition'
            : `${String(offeneTermine)} Positionen warten noch auf die Disposition`}{' '}
          — auf dem Dokument steht dort „Termin auf Anfrage". Versenden ist möglich; die
          Zusage kann nachgereicht werden.
          {/* CRM-118 (L7): der Weg zu den Anfragen, wo das Recht dafür da ist (ANG-38). */}
          {beiOptionsanfragen !== undefined && (
            <>
              {' '}
              <Maskenverweis maske={{ art: 'optionsanfragen' }} beiKlick={beiOptionsanfragen}>
                Zu den Anfragen
              </Maskenverweis>
            </>
          )}
        </p>
      )}

      {/*
        EINE KNOPFREIHE UND KEINE KANALZEILE (Vorlage, 14.09.2026).

        `.kanalzeile` ist ein Formularraster — 7 rem Beschriftung, zwei
        Feldspalten, eine Handlung. Hier standen drei Knöpfe darin: Der erste
        bekam die 7-rem-Spalte und brach in drei Zeilen um („Auf
        ‚angenommen' setzen"), der zweite dehnte sich über zwei Fünftel der
        Karte. Die Vorlage setzt Schaltflächen in ihrer Inhaltsbreite
        nebeneinander — das tut `.knopfreihe`.
      */}
      {/*
        DER HAUPTKNOPF STEHT ZULETZT (CRM-87). Die kleinen Status- und
        Versionsknöpfe stehen davor, „Versenden" bzw. „In Auftrag übernehmen"
        ganz rechts. Beide erscheinen nie zugleich: Versendet wird aus dem
        Entwurf und der Verhandlung, übernommen nur das angenommene Angebot,
        und aus „angenommen" führt keine Kante (fach.angebotsuebergang).
      */}
      <div className="knopfreihe">
        {wechselbar &&
          angebot.moeglicheStatus
            .filter((s) => s !== 'versendet')
            .map((s) =>
              s === 'abgelehnt' ? (
                <button
                  key={s}
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => setzeAblehnung(true)}
                >
                  Ablehnen …
                </button>
              ) : (
                <button
                  key={s}
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => {
                    if (s === 'angenommen') void annahmeVorbereiten();
                    else beiStatus(s, {});
                  }}
                >
                  {`Auf „${STATUSNAME[s]}“ setzen`}
                </button>
              ),
            )}

        {/*
          ANG-15: Eine neue Version entsteht erst NACH dem Versand. Beim
          Entwurf steht der Knopf deshalb nicht da — er waere ein Angebot,
          das der Server ablehnt. Ebenso, wenn schon ein Auftrag besteht (ANG-18).
        */}
        {darfAendern && !angebot.eingefroren && angebot.status !== 'entwurf'
          && angebot.auftrag === null && (
          <button type="button" className="knopf knopf--klein" onClick={beiNeueVersion}>
            Neue Version
          </button>
        )}
        {wechselbar && angebot.moeglicheStatus.includes('versendet') && (
          <button
            type="button"
            className="knopf knopf--haupt"
            onClick={() => setzeRueckfrage('versenden')}
          >
            Versenden …
          </button>
        )}
        {uebernehmbar && (
          <button
            type="button"
            className="knopf knopf--haupt"
            onClick={() => setzeUebernahme(!uebernahme)}
          >
            {uebernahme ? 'Übernahme schließen' : 'In Auftrag übernehmen'}
          </button>
        )}
      </div>

      {ablehnung && (
        <div className="seitenblock__maske">
          <div className="feldreihe feldreihe--halb">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ablehnungsgrund">
                Grund
              </label>
              <select
                id="ablehnungsgrund"
                className="feld__eingabe"
                value={grund}
                onChange={(e) => setzeGrund(e.target.value)}
              >
                <option value="">Bitte wählen …</option>
                {gruende
                  .filter((g) => g.aktiv)
                  .map((g) => (
                    <option key={g.schluessel} value={g.schluessel}>
                      {g.bezeichnung}
                    </option>
                  ))}
              </select>
              {grund === '' && (
                <p className="feld__hilfe">
                  Ohne Grund bliebe die Auswertung „warum verlieren wir“ leer, und niemand sähe es.
                </p>
              )}
            </div>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="ablehnungstext">
                Anmerkung
              </label>
              <input
                id="ablehnungstext"
                className="feld__eingabe"
                type="text"
                value={text}
                onChange={(e) => setzeText(e.target.value)}
                placeholder="optional"
              />
            </div>
          </div>
          <Abschlussreihe
            abbruch={
              <button type="button" className="knopf" onClick={() => setzeAblehnung(false)}>
                Abbrechen
              </button>
            }
          >
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={grund === ''}
              onClick={() => setzeRueckfrage('ablehnung')}
            >
              Ablehnung festhalten …
            </button>
          </Abschlussreihe>
        </div>
      )}

      {/*
        ANG-15: Versenden setzt den Status; das Dokument selbst geht über den
        Postausgang im Abschnitt „Beleg". Das Pop-up verspricht deshalb keine Mail.
      */}
      {rueckfrage === 'versenden' && (
        <Rueckfrage
          frage="Angebot versenden?"
          unterzeile={
            angebot.version > 1
              ? `Angebot ${angebot.angebotsnummer} · Version ${String(angebot.version)}`
              : `Angebot ${angebot.angebotsnummer}`
          }
          tat="Versenden"
          beiSchliessen={() => setzeRueckfrage(null)}
          beiBestaetigen={async () => {
            await beiStatusBestaetigt('versendet', {});
            setzeRueckfrage(null);
          }}
        >
          <p>
            Das Angebot über <strong>{alsEuro(angebot.summen.belegbrutto)}</strong> an{' '}
            {angebot.kundenname}
            {angebot.ansprechpartnerName !== null && ` (${angebot.ansprechpartnerName})`} gilt
            danach als versendet. Jede weitere Änderung legt eine neue Fassung an; die versendete
            bleibt einsehbar.
          </p>
          {/* ANG-36 */}
          {offeneTermine > 0 && (
            <p className="warnung">
              {offeneTermine === 1
                ? 'Bei einer Position steht'
                : `Bei ${String(offeneTermine)} Positionen steht`}{' '}
              „Termin auf Anfrage".
            </p>
          )}
        </Rueckfrage>
      )}

      {rueckfrage === 'ablehnung' && (
        <Rueckfrage
          frage="Angebot ablehnen?"
          unterzeile={`Angebot ${angebot.angebotsnummer}`}
          tat="Ablehnung festhalten"
          beiSchliessen={() => setzeRueckfrage(null)}
          beiBestaetigen={async () => {
            await beiStatusBestaetigt('abgelehnt', {
              ablehnungsgrund: grund,
              ablehnungstext: text === '' ? null : text,
            });
            setzeRueckfrage(null);
            setzeAblehnung(false);
          }}
        >
          <p>
            Das Angebot über <strong>{alsEuro(angebot.summen.belegbrutto)}</strong> an{' '}
            {angebot.kundenname} wird als abgelehnt festgehalten. Grund: „
            {gruende.find((g) => g.schluessel === grund)?.bezeichnung ?? grund}“
            {text !== '' && ` — ${text}`}.
          </p>
          <p>
            Der Status lässt sich danach nicht zurücksetzen; wer sich vertan hat, legt eine neue
            Version an.
          </p>
        </Rueckfrage>
      )}

      {/*
        DIE WAHL BEI DER ANNAHME (ANG-39, ANG-31).

        „Höchstens einer von ihnen wird zum Auftrag, die übrigen verfallen mit
        der Annahme." Was hier gewählt wird, entscheidet über zwei Termine: Der
        eine wird verbindlich, der andere wird abgesagt und gibt seine Plätze
        frei. Deshalb steht der Satz darüber, und deshalb ist die Wahl kein
        Häkchen, sondern eine Entscheidung je Anfrage.
      */}
      {zuWaehlen.length > 0 && (
        <div className="seitenblock__maske">
          <p className="warnung">
            {zuWaehlen.length === 1
              ? 'Zu dieser Leistung sind mehrere Termine zugesagt.'
              : `Zu ${String(zuWaehlen.length)} Leistungen sind mehrere Termine zugesagt.`}{' '}
            Wählen Sie, welchen der Kunde genommen hat. Die übrigen werden abgesagt und geben
            ihre Plätze sofort frei.
          </p>
          {zuWaehlen.map((a) => (
            <fieldset
              key={a.id}
              className="feld wahlgruppe"
              aria-label={`Termin für ${a.leistung}`}
            >
              <legend className="feld__beschriftung">
                {a.leistung} · {a.teilnehmerzahl} Teilnehmer
              </legend>
              <div className="wahlliste">
                {a.alternativen
                  .filter((alt) => alt.antwort === 'zugesagt')
                  .map((alt) => (
                    <label key={alt.id}>
                      <input
                        type="radio"
                        name={`terminwahl-${a.id}`}
                        value={alt.id}
                        checked={terminwahl[a.id] === alt.id}
                        onChange={() =>
                          setzeTerminwahl((bisher) => ({ ...bisher, [a.id]: alt.id }))
                        }
                      />{' '}
                      {alsTag(alt.wunschtag)}
                      {alt.wunschzeit !== null && `, ${alt.wunschzeit} Uhr`}
                      {alt.fristBis !== null && (
                        <span className="feld__hilfe"> · gehalten bis {alsTag(alt.fristBis)}</span>
                      )}
                    </label>
                  ))}
              </div>
            </fieldset>
          ))}
          <Abschlussreihe
            abbruch={
              <button type="button" className="knopf" onClick={() => setzeZuWaehlen([])}>
                Abbrechen
              </button>
            }
          >
            <button
              type="button"
              className="knopf knopf--haupt"
              onClick={() => {
                beiStatus(
                  'angenommen',
                  {},
                  Object.entries(terminwahl).map(([anfrage, alternative]) => ({
                    anfrage,
                    alternative,
                  })),
                );
                setzeZuWaehlen([]);
              }}
            >
              Annahme festhalten
            </button>
          </Abschlussreihe>
        </div>
      )}

      {/*
        ANG-18 und ANG-19. Der Regelfall ist die vollstaendige Uebernahme —
        deshalb ist die Auswahl LEER vorbelegt und heisst „alles Wirksame".
        Wer einzelne Zeilen ankreuzt, uebernimmt nur diese; der Rest bleibt
        nach ANG-19 am Angebot offen.
      */}
      {/*
        EIN EIGENER ABSCHNITT UND KEIN ANGABENRASTER (Auftrag CRM-190).
        `.angaben` hat zwei Spalten, Beschriftung | Wert: Der Hilfetext füllte
        die erste in voller Breite, Liste und Knöpfe fielen in die zweite, die
        auf null zusammenschrumpfte — sie standen Wort für Wort umbrochen
        rechts neben der Karte, und die Seite rollte waagrecht.
      */}
      {uebernahme && (
        <div className="seitenblock__maske">
          <fieldset className="feld wahlgruppe" aria-describedby="uebernahme-hilfe">
            <legend className="feld__beschriftung">Positionen zur Übernahme</legend>
            {/* PRF-16 */}
            <p id="uebernahme-hilfe" className="feld__hilfe">
              Ohne Auswahl werden alle wirksamen Positionen übernommen. Die Preise wandern
              eingefroren mit — eine zwischenzeitliche Preisänderung bewegt den Auftrag
              nicht. Alternative und optionale Positionen bleiben liegen.
            </p>
            <div className="wahlliste wahlliste--betraege">
              {waehlbar.map((p) => (
                <label key={p.id}>
                  <input
                    type="checkbox"
                    checked={gewaehlt.includes(p.id)}
                    onChange={(e) =>
                      setzeGewaehlt((bisher) =>
                        e.target.checked
                          ? [...bisher, p.id]
                          : bisher.filter((k) => k !== p.id),
                      )
                    }
                  />
                  <span>{p.bezeichnung}</span>
                  <span className="wahlliste__betrag">
                    {p.netto !== null && alsEuro(p.netto)}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <Abschlussreihe
            abbruch={
              <button type="button" className="knopf" onClick={() => setzeUebernahme(false)}>
                Abbrechen
              </button>
            }
          >
            <button
              type="button"
              className="knopf knopf--haupt"
              onClick={() => {
                beiUebernahme(gewaehlt);
                setzeUebernahme(false);
              }}
            >
              {gewaehlt.length === 0 ? 'Vollständig übernehmen' : 'Auswahl übernehmen'}
            </button>
          </Abschlussreihe>
        </div>
      )}

    </section>
  );
}

/**
 * Die Positionstafel (ANG-02 bis ANG-05).
 *
 * DIE WERTUNG STEHT ALS EIGENE SPALTE und nicht als Farbe: ANG-03 verlangt,
 * dass optionale und alternative Positionen „klar als solche ausgewiesen"
 * werden — und WCAG 1.4.1 verlangt, dass das auch ohne Farbunterscheidung
 * lesbar ist.
 */
function Positionen({
  angebot,
  bearbeitbar,
  beiAnlegen,
  beiLoeschen,
  beiOrdnen,
}: {
  readonly angebot: Angebot;
  readonly bearbeitbar: boolean;
  readonly beiAnlegen: (eingabe: Parameters<typeof positionAnlegen>[1]) => void;
  /** Wirft bei einem Fehler; die Rückfrage zeigt ihn (CRM-103). */
  readonly beiLoeschen: (position: string) => Promise<void>;
  readonly beiOrdnen: (reihenfolge: readonly string[]) => void;
}): JSX.Element {
  const [offen, setzeOffen] = useState<string | undefined>(undefined);
  const [zuEntfernen, setzeZuEntfernen] = useState<
    { readonly position: Angebotsposition; readonly nummer: number } | undefined
  >(undefined);

  /*
   * DIE EXKLUSIVTERMINE DIESES KUNDEN — nur noch zum Ausschreiben der Zeilen.
   *
   * ZWEI ZUSTAENDE, DIE GLEICH AUSSAHEN.
   *
   * `catch` setzte die Liste auf leer, und die Maske schrieb „keiner
   * vereinbart" — dasselbe Wort wie fuer „es gibt wirklich keinen". Der
   * Anwender konnte nicht unterscheiden, ob nichts vereinbart ist, ob ihm das
   * Recht `termin.sehen` fehlt oder ob die Auskunft nicht antwortet (ERR-01).
   *
   * SEIT DEM 15.09.2026 DIENT DIE LISTE NUR NOCH DER ANZEIGE. Der Terminwähler
   * ist weg (siehe „Position hinzufügen"); geblieben ist die Zeile, die zu
   * einer angenommenen Position den Termin beim Namen nennt. Ein eigener
   * Fehlerzustand braucht es dafür nicht mehr: Bleibt die Liste leer, sagt die
   * Zeile „Termin vereinbart" statt eines Datums — und niemand trifft auf
   * dieser Grundlage eine Entscheidung.
   */
  const [termine, setzeTermine] = useState<readonly Exklusivtermin[]>([]);
  useEffect(() => {
    let verworfen = false;
    void exklusive(angebot.kunde)
      .then((e) => {
        if (verworfen) return;
        setzeTermine(e);
      })
      .catch(() => {
        if (verworfen) return;
        setzeTermine([]);
      });
    return () => {
      verworfen = true;
    };
  }, [angebot.kunde]);

  /** Eine Position um eine Stelle verschieben (ANG-04). */
  function verschiebe(index: number, richtung: -1 | 1): void {
    const ids = angebot.positionen.map((p) => p.id);
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= ids.length) return;
    const kopie = [...ids];
    [kopie[index], kopie[ziel]] = [kopie[ziel]!, kopie[index]!];
    beiOrdnen(kopie);
  }

  return (
    <div className="tafel__abschnitte">
      <h2 className="nur-vorlesen">Positionen</h2>
      <div className="tafel--weit">
        <table className="liste__tabelle">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Art</th>
              <th scope="col">Bezeichnung</th>
              <th scope="col">Wertung</th>
              <th scope="col" className="zahl">
                Menge
              </th>
              <th scope="col" className="zahl">
                Einzelpreis
              </th>
              <th scope="col">Steuer</th>
              <th scope="col" className="zahl">
                Netto
              </th>
              <th scope="col">Handlung</th>
            </tr>
          </thead>
          <tbody>
            {angebot.positionen.map((p, i) => (
              <Positionszeile
                key={p.id}
                position={p}
                termine={termine}
                nummer={i + 1}
                bearbeitbar={bearbeitbar}
                erste={i === 0}
                letzte={i === angebot.positionen.length - 1}
                offen={offen === p.id}
                beiAufklappen={() => setzeOffen(offen === p.id ? undefined : p.id)}
                beiHoch={() => verschiebe(i, -1)}
                beiRunter={() => verschiebe(i, 1)}
                beiLoeschen={() => setzeZuEntfernen({ position: p, nummer: i + 1 })}
              />
            ))}
          </tbody>
        </table>
      </div>

      {zuEntfernen !== undefined && (
        <Rueckfrage
          frage="Position entfernen?"
          unterzeile={`Position ${String(zuEntfernen.nummer)} · ${zuEntfernen.position.bezeichnung}`}
          tat="Entfernen"
          beiSchliessen={() => setzeZuEntfernen(undefined)}
          beiBestaetigen={async () => {
            await beiLoeschen(zuEntfernen.position.id);
            setzeZuEntfernen(undefined);
          }}
        >
          <p>
            {zuEntfernen.position.bezeichnung},{' '}
            {alsMengenangabe(zuEntfernen.position.menge, zuEntfernen.position.mengeneinheit)}, netto{' '}
            <strong>{alsEuro(zuEntfernen.position.netto)}</strong>, wird aus dem Angebot entfernt.
          </p>
          {/* ANG-15 */}
          {angebot.status !== 'entwurf' && (
            <p>
              Das Angebot ist schon versendet: Dafür entsteht eine neue Fassung; die bisherige
              bleibt unverändert einsehbar.
            </p>
          )}
        </Rueckfrage>
      )}

      {angebot.positionen.length === 0 && (
        <p className="feld__hilfe">
          Noch keine Position. Ein Angebot ohne berechnete Position geht nicht hinaus.
        </p>
      )}

      {bearbeitbar && (
        <NeuePosition
          kunde={angebot.kunde}
          kundenart={angebot.kundenart}
          beiAnlegen={beiAnlegen}
        />
      )}
    </div>
  );
}

function Positionszeile({
  position,
  termine,
  nummer,
  bearbeitbar,
  erste,
  letzte,
  offen,
  beiAufklappen,
  beiHoch,
  beiRunter,
  beiLoeschen,
}: {
  readonly position: Angebotsposition;
  readonly termine: readonly Exklusivtermin[];
  readonly nummer: number;
  readonly bearbeitbar: boolean;
  readonly erste: boolean;
  readonly letzte: boolean;
  readonly offen: boolean;
  readonly beiAufklappen: () => void;
  readonly beiHoch: () => void;
  readonly beiRunter: () => void;
  readonly beiLoeschen: () => void;
}): JSX.Element {
  return (
    <>
      <tr>
        <td>{nummer}</td>
        <td>{ARTNAME[position.art]}</td>
        <td>
          {position.bezeichnung}
          {position.artikelnummer !== null && (
            <span className="kanalliste__name"> · {position.artikelnummer}</span>
          )}
          {/*
            PRD-42: Die Vertriebsform steht an der Zeile, sobald sie nicht der
            Regelfall ist. „Einzelplatz" auszuschreiben waere Laerm — an jeder
            Zeile dasselbe Wort. Der Exklusivtermin ist die Ausnahme, und
            Ausnahmen gehoeren benannt.
          */}
          {position.vertriebsform !== null && position.vertriebsform !== 'einzelplatz' && (
            <span className="marke">{VERTRIEBSFORMNAME[position.vertriebsform]}</span>
          )}
          {/*
            ANG-36: „Termin auf Anfrage" — SICHTBAR und nicht von Hand gesetzt.
            Solange die Disposition nicht zugesagt hat, steht es hier und auf
            dem Dokument; mit der Zusage steht dort der Termin samt Haltedatum.
          */}
          {position.optionsstand === 'offen' && (
            <div className="feld__hilfe">
              <span className="marke marke--warn">Termin auf Anfrage</span>
            </div>
          )}
          {position.optionsstand === 'abgelehnt' && (
            <div className="feld__hilfe">
              <span className="marke marke--krit">Termin abgelehnt</span>
            </div>
          )}
          {position.optionsstand === 'vorschlag' && (
            <div className="feld__hilfe">
              <span className="marke marke--warn">Gegenvorschlag der Disposition</span>
            </div>
          )}
          {/*
            ANG-36, ANG-25: DATUM UND UHRZEIT — dieselbe Angabe wie auf dem
            Kundendokument.

            Auftraggeber, 13.09.2026: „im angebot soll im dokument für den
            kunden auch bei jedem termin das datum und die uhrzeit stehen." Wer
            das Angebot am Bildschirm prüft, soll dasselbe lesen wie der Kunde
            — sonst prüft er etwas anderes, als er verschickt.
          */}
          {position.terminBeginn !== null && (
            <div className="feld__hilfe">
              Termin: {zeitpunkt(position.terminBeginn)} Uhr
              {position.terminGehaltenBis !== null
                && ` — gehalten bis ${nurTag(position.terminGehaltenBis)}`}
            </div>
          )}
          {position.terminBeginn === null && position.wunschtermine.length > 0 && (
            <div className="feld__hilfe">
              Wunsch:{' '}
              {position.wunschtermine
                .map((w) => (w.zeit === null ? nurTag(w.tag) : `${nurTag(w.tag)}, ${w.zeit} Uhr`))
                .join(' oder ')}
            </div>
          )}
          {position.bezugsart === 'termin' && position.terminBeginn === null && (
            /* `terminbezug` nennt sich selbst — und zeigt nie eine Kennung. */
            <div className="feld__hilfe">{terminbezug(position, termine)}</div>
          )}
          {position.bezugsart === 'zeitraum' && position.bezugVon !== null && (
            <div className="feld__hilfe">
              {/* `nurTag` und nicht die rohe Zeichenkette: „2026-12-04" ist kein Datum,
                  das jemand liest. Die Datei führt den Helfer schon. */}
              Zeitraum: {nurTag(position.bezugVon)}
              {position.bezugBis !== null && ` bis ${nurTag(position.bezugBis)}`}
            </div>
          )}
        </td>
        <td>
          {/* ANG-03: klar als solche ausgewiesen — als Text, nicht als Farbe. */}
          {position.wertung === 'wirksam' ? (
            <span className="feld__hilfe">zählt</span>
          ) : (
            <span className="marke marke--inaktiv">{WERTUNGSNAME[position.wertung]}</span>
          )}
        </td>
        <td className="zahl">{alsMengenangabe(position.menge, position.mengeneinheit)}</td>
        <td className="zahl">
          {alsEuro(position.einzelpreis)}
          {/*
            PRD-12: DASS DER PREIS DIE STEUER ENTHAELT, MUSS DASTEHEN.
            Der Katalog des Hauses ist brutto gepflegt, der Geschäftskundenbeleg
            führt netto — ohne diese Marke steht „1 × 3.200,00 €" neben einem
            Netto von 2.689,07 €, und der Zusammenhang ist nicht zu sehen.
          */}
          {position.istBrutto === true && (
            <span className="marke marke--inaktiv">brutto</span>
          )}
          {position.preisUebersteuert && (
            <span className="marke marke--inaktiv">von Hand</span>
          )}
        </td>
        <td>{position.steuerschluessel ?? '—'}</td>
        <td className="zahl">{alsEuro(position.netto)}</td>
        <td>
          {position.herleitung.length > 0 && (
            <button type="button" className="knopf knopf--klein" onClick={beiAufklappen}>
              {offen ? 'Herleitung zu' : 'Herleitung'}
            </button>
          )}
          {bearbeitbar && (
            <>
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={erste}
                onClick={beiHoch}
                aria-label={`Position ${nummer} nach oben`}
              >
                ↑
              </button>
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={letzte}
                onClick={beiRunter}
                aria-label={`Position ${nummer} nach unten`}
              >
                ↓
              </button>
              <button type="button" className="knopf knopf--eingriff knopf--klein" onClick={beiLoeschen}>
                Entfernen …
              </button>
            </>
          )}
        </td>
      </tr>

      {/* PRF-14: Die Preisherleitung ist in der Oberflaeche einsehbar. */}
      {offen && (
        <tr>
          <td colSpan={9}>
            <p className="feld__hilfe">
              Preisherkunft: {position.preisherkunft ?? 'unbekannt'}
              {position.hoechstrabattProzent !== null
                && ` · Höchstrabatt ${position.hoechstrabattProzent} %`}
            </p>
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle">
                <thead>
                  <tr>
                    <th scope="col">Stufe</th>
                    <th scope="col">Regel</th>
                    <th scope="col" className="zahl">
                      davor
                    </th>
                    <th scope="col" className="zahl">
                      danach
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {position.herleitung.map((h, i) => (
                    <tr key={`${h.stufe}-${h.regelkennung}-${String(i)}`}>
                      <td>{h.stufe}</td>
                      <td>
                        {h.regelart}
                        <span className="kanalliste__name"> · {h.regelkennung}</span>
                        {h.gekappt === true && <span className="marke marke--gesperrt">gekappt</span>}
                      </td>
                      <td className="zahl">{h.wertDavor}</td>
                      <td className="zahl">{h.wertDanach}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Die Eingabe einer neuen Position.
 *
 * DIE ARTIKELSUCHE STEHT DRIN und nicht daneben: ANG-05 findet den Preis aus
 * dem Katalog, und ein Angebot, dessen Positionen von Hand getippt werden,
 * verliert genau das. Die Freitextposition bleibt fuer das, was nicht im
 * Katalog steht.
 */
function NeuePosition({
  kunde,
  kundenart,
  beiAnlegen,
}: {
  /* Fuer die Preisauskunft (ANG-05) und die Terminauswahl (KAP-04, BUC-105). */
  readonly kunde: string;
  readonly kundenart: string;
  readonly beiAnlegen: (eingabe: Parameters<typeof positionAnlegen>[1]) => void;
}): JSX.Element {
  const [art, setzeArt] = useState<Positionsart>('artikel');
  const [wertung, setzeWertung] = useState<Wertung>('wirksam');
  const [artikel, setzeArtikel] = useState<Artikelzeile | undefined>(undefined);
  /* Die Auswahl liegt seit dem 06.09.2026 in einem Fenster, nicht unter dem Feld. */
  const [wahlOffen, setzeWahlOffen] = useState(false);
  const [form, setzeForm] = useState<Vertriebsform | undefined>(undefined);
  /*
   * ANG-25: „jeweils mit ZEITRAUM und Ressourcenbezug". Beim Angebot ist der
   * Termin meist noch nicht angelegt — nach BUC-68 entsteht er erst aus dem
   * angenommenen Angebot. Was der Kunde bekommt, ist ein DATUM, und genau das
   * fehlte: Die Spalten stehen seit Migration 0108 da, die Schnittstelle nimmt
   * sie an, und die Maske hatte kein Feld dafür.
   */
  const [von, setzeVon] = useState('');
  /*
   * DIE TERMINOPTION ENTSTEHT MIT DER ZEILE (ANG-30, ANG-31).
   *
   * Auftraggeber, 13.09.2026: „terminoptionen sollen direkt mit dem anlegen
   * eines artikels im angebot angelegt werden" — und „die uhrzeit soll im
   * angebot mit angegeben werden. kunden haben häufig feste vorstellungen."
   *
   * Der erste Wunschtermin ist das Feld, das es schon gab (`von`); die Uhrzeit
   * steht daneben, die Alternativen darunter. Höchstens drei insgesamt: Jede
   * hält Plätze, solange sie zugesagt ist (ANG-31, Tab. 29).
   */
  /*
   * DIE UHRZEIT STEHT AUF 08:00 UND NICHT AUF „TAGESBEGINN".
   *
   * Auftraggeber, 16.09.2026: „Als Startuhrzeit den Eintrag Tagesbeginn
   * entfernen und immer mit der Auswahl 8:00 Uhr anfangen."
   *
   * „Tagesbeginn" hiess: keine Uhrzeit mitschicken, der Server nimmt die des
   * Standorts. Das ist dieselbe Zeit — nur steht sie dann nirgends, und wer
   * das Angebot prueft, sieht einen Wunschtermin ohne Uhrzeit. Seit AeA-26
   * entscheidet die Uhrzeit ueber Trainer, Flaeche und darueber, was danach
   * noch auf den Tag passt; sie gehoert dahin, wo man sie liest.
   */
  const [vonZeit, setzeVonZeit] = useState<string>(ERSTE_RASTERZEIT);
  const [alternativen, setzeAlternativen] = useState<readonly { tag: string; zeit: string }[]>(
    [],
  );

  /*
   * DER OFFENE TERMIN — nur beim EINZELPLATZ.
   *
   * Auftraggeber, 16.09.2026: „Beim Artikel Fahrtraining und Auswahl
   * einzelplatz soll es die Moeglichkeit geben, keinen Termin anzugeben, oder
   * einen offenen Termin fuer dieses Produkt auszuwaehlen, mit Anzeige der
   * freien Plaetze."
   *
   * `buchbare` liefert nach BUC-67 ausschliesslich OFFENE Termine — ein
   * Exklusivtermin gehoert einem Kunden ganz und traegt keine Einzelplaetze.
   * `frei` rechnet die Auskunft mit Anfragendem und Kundenart (KAP-04,
   * BUC-105): Ein Kunde mit eigenem Kontingent bekaeme sonst „voll" gemeldet.
   */
  const [offenerTermin, setzeOffenerTermin] = useState('');
  const [offeneTermine, setzeOffeneTermine] = useState<readonly Terminauswahl[]>([]);
  const [terminfehler, setzeTerminfehler] = useState(false);
  const [teilnehmer, setzeTeilnehmer] = useState('');
  const [optionsort, setzeOptionsort] = useState('');
  const [optionsfrist, setzeOptionsfrist] = useState('');
  /*
   * Die Standorte für die Option: Der Wunschtermin wird ein Termin im
   * Kalender, und ein Termin gehört zu einem Ort — er bestimmt Tagesrahmen,
   * Blockraster und die Ressourcen, gegen die die Disposition ihn hält.
   */
  const [orte, setzeOrte] = useState<readonly { kennung: string; bezeichnung: string }[]>([]);

  useEffect(() => {
    void (async () => {
      try {
        const antwort = await standorte();
        setzeOrte(antwort.standorte);
      } catch {
        /* Ohne Auskunft bleibt die Liste leer — der Server nimmt den Hauptstandort. */
      }
    })();
  }, []);
  const [bezeichnung, setzeBezeichnung] = useState('');
  const [menge, setzeMenge] = useState('1');
  const [einzelpreis, setzeEinzelpreis] = useState('');
  const [rabatt, setzeRabatt] = useState('');
  const [steuersatz, setzeSteuersatz] = useState('DE-19');

  /*
   * DER PREIS, BEVOR DIE ZEILE ENTSTEHT (ANG-05).
   *
   * Auftraggeber, 16.09.2026: „Der Artikelpreis soll angezeigt werden, mit der
   * Option ihn zu ueberschreiben, bzw. einen Rabatt in Prozent anzugeben."
   *
   * Bis dahin stand im Preisfeld „Preis aus dem Katalog" — und was das war,
   * erfuhr man erst an der fertigen Zeile. Wer schnell einen Betrag nennen
   * wollte, musste die Position anlegen, nachsehen und wieder loeschen.
   *
   * `preisauskunft` rechnet dieselbe Kette wie der Beleg: Preisliste, Staffel,
   * Kundenkondition, Standort des Termins (AA-011) und, beim Exklusivtermin,
   * die vereinbarte Pauschale (DAT-08, PRD-46). Sie ANTWORTET NUR, sie legt
   * nichts an.
   */
  const [auskunft, setzeAuskunft] = useState<Preisauskunft | undefined>(undefined);
  const [preisLaeuft, setzePreisLaeuft] = useState(false);

  /*
   * DIE GEWAEHLTE FORM, mit der Vorbelegung des Artikels.
   *
   * Hat ein Produkt genau eine Form, gibt es nichts zu waehlen und der
   * Dienst nimmt ohnehin die erste (PRD-42). Hat es mehrere, entscheidet der
   * Anwender — und daran haengt mehr als der Preis: AUF-04 leitet die
   * Positionsart daraus ab und AUF-21 den Fakturierungszeitpunkt.
   */
  const formen = artikel?.vertriebsformen ?? [];
  const gewaehlteForm: Vertriebsform | undefined = form ?? formen[0];
  const zurWahl = formen.length > 1;

  /*
   * PRD-46, E-49: BEIM EXKLUSIVTERMIN IST DIE MENGE EINS.
   *
   * „Der Rechnungsbetrag eines Exklusivtermins IST die vereinbarte Pauschale.
   * […] Eine Abrechnung nach Köpfen ist bei Exklusivterminen nicht
   * vorgesehen." Die Gruppe hat zwölf Köpfe, die Zeile hat eine Pauschale.
   *
   * Bis zum 15.09.2026 hing diese Regel am GEWÄHLTEN Termin — sie griff also
   * nur, wenn dort schon eine Pauschale vereinbart war. Mit dem Terminwähler
   * ist sie an die Vertriebsform gewandert, wo sie hingehört: Sie gilt für
   * jeden Exklusivverkauf, auch für den ersten.
   */
  const pauschal = gewaehlteForm === 'exklusivtermin';

  /*
   * DIE OFFENEN TERMINE DIESES PRODUKTS — nur, wenn Einzelplaetze verkauft
   * werden. Bei jeder anderen Form waere die Liste eine Einladung, einen
   * Teilnehmerplatz an einen Termin zu binden, dessen Kapazitaet einem Kunden
   * ganz gehoert (BUC-68); die Ablage weist das seit Migration 0110 ab.
   */
  const artikelId = artikel?.id;
  const einzelplatz = gewaehlteForm === 'einzelplatz';
  useEffect(() => {
    if (artikelId === undefined || !einzelplatz) {
      setzeOffeneTermine([]);
      setzeTerminfehler(false);
      return undefined;
    }
    let verworfen = false;
    buchbare(artikelId, undefined, kunde, kundenart)
      .then((liste) => {
        if (verworfen) return;
        setzeOffeneTermine(liste);
        setzeTerminfehler(false);
      })
      .catch(() => {
        if (verworfen) return;
        setzeOffeneTermine([]);
        /* ERR-01: „keiner da" und „nicht erreichbar" sind zweierlei. */
        setzeTerminfehler(true);
      });
    return () => {
      verworfen = true;
    };
  }, [artikelId, einzelplatz, kunde, kundenart]);

  /*
   * DER PREIS ZUR AKTUELLEN EINGABE (ANG-05).
   *
   * Er haengt an Artikel, Verkaufsform, Menge und — wenn einer gewaehlt ist —
   * am Termin. Die Frist von einer Viertelsekunde ist keine Zierde: Die Menge
   * wird getippt, und ohne sie liefe je Tastendruck ein Aufruf.
   */
  const preisMenge = pauschal ? 1 : Number(menge);
  useEffect(() => {
    if (artikelId === undefined || gewaehlteForm === undefined || art !== 'artikel') {
      setzeAuskunft(undefined);
      return undefined;
    }
    let verworfen = false;
    setzePreisLaeuft(true);
    const uhr = setTimeout(() => {
      void preisauskunft(artikelId, {
        kunde,
        vertriebsform: gewaehlteForm,
        menge: Number.isFinite(preisMenge) && preisMenge > 0 ? preisMenge : 1,
        termin: offenerTermin === '' ? null : offenerTermin,
      })
        .then((a) => {
          if (!verworfen) setzeAuskunft(a);
        })
        .catch(() => {
          if (!verworfen) setzeAuskunft(undefined);
        })
        .finally(() => {
          if (!verworfen) setzePreisLaeuft(false);
        });
    }, 250);
    return () => {
      verworfen = true;
      clearTimeout(uhr);
    };
  }, [artikelId, gewaehlteForm, art, kunde, preisMenge, offenerTermin]);

  /*
   * Die Wunschtermine dieser Zeile: der erste plus die Alternativen, ohne
   * leere. Die Uhrzeit GEHT IMMER MIT — die Maske bietet seit dem 16.09.2026
   * keinen leeren Eintrag mehr an. Die Schnittstelle nimmt weiter `null`
   * entgegen; Bestandsanfragen ohne Uhrzeit bleiben damit lesbar.
   */
  const wunschtermine = [
    ...(von === '' ? [] : [{ tag: von, zeit: vonZeit === '' ? null : vonZeit }]),
    ...alternativen
      .filter((a) => a.tag !== '')
      .map((a) => ({ tag: a.tag, zeit: a.zeit === '' ? null : a.zeit })),
  ];

  const braucthBetrag = art === 'artikel' || art === 'freitext' || art === 'rabatt';
  /*
   * OHNE TEILNEHMERZAHL KEINE ANFRAGE (ANG-30).
   *
   * Die Disposition hält Plätze — wie viele, muss dastehen. Die Menge der
   * Zeile taugt dafür nicht: Bei einem Exklusivtermin ist sie eins (die
   * Pauschale, PRD-46), und die Gruppe hat trotzdem zwölf Köpfe.
   */
  const bereit =
    (art === 'artikel'
      ? artikel !== undefined && menge !== ''
      : braucthBetrag
        ? bezeichnung !== '' && menge !== '' && einzelpreis !== ''
        : bezeichnung !== '')
    && (wunschtermine.length === 0 || Number(teilnehmer) > 0);

  function anlegen(): void {
    /*
     * DER ABZUG WIRD HIER NEGIERT, nicht vom Anwender getippt.
     *
     * Wer einen Nachlass erfasst, schreibt "150,00" — nicht "−150,00". Die
     * Ablage verlangt aber das Minus (0031), weil eine Rabattzeile ihrem
     * Wesen nach negativ ist. Das Vorzeichen an EINER Stelle zu setzen ist
     * verlaesslicher, als es dem Anwender aufzutragen: Ein vergessenes
     * Minus waere sonst ein Zuschlag, den der Server abweist — und der
     * Anwender saehe eine Meldung ueber ein Zeichen.
     */
    const preis =
      art === 'rabatt' && einzelpreis !== '' && !einzelpreis.startsWith('-')
        ? `-${einzelpreis}`
        : einzelpreis;

    beiAnlegen({
      art,
      wertung,
      ...(art === 'artikel' ? { artikel: artikel?.id ?? null } : {}),
      ...(art === 'artikel' && zurWahl && gewaehlteForm !== undefined
        ? { vertriebsform: gewaehlteForm }
        : {}),
      /*
       * DREI WEGE, UND SIE SCHLIESSEN EINANDER AUS.
       *
       *   EINZELPLATZ auf einem GEWAEHLTEN offenen Termin — der Termin steht
       *     schon, die Zeile zeigt auf ihn (bezugsart „termin").
       *   EXKLUSIVTERMIN mit WUNSCHDATUM — der Termin entsteht erst mit der
       *     Annahme (BUC-68, ANG-27); bis dahin traegt die Zeile das Datum
       *     (bezugsart „zeitraum", die einzige, die bezugVon zulaesst).
       *   OHNE BEIDES — eine Zeile mit ihrem Preis und sonst nichts.
       *
       * Der dritte Weg ist seit dem 16.09.2026 ausdruecklich vorgesehen:
       * „damit auch mal schnell ein Preis an den Kunden geschickt werden kann,
       * ohne einen Termin anzulegen."
       */
      ...(art === 'artikel' && offenerTermin !== ''
        ? { bezugsart: 'termin' as const, bezugId: offenerTermin }
        : art === 'artikel' && von !== ''
          ? { bezugsart: 'zeitraum' as const, bezugVon: von }
          : {}),
      ...(bezeichnung === '' ? {} : { bezeichnung }),
      /*
       * PRD-46: "Der Rechnungsbetrag eines Exklusivtermins IST die
       * vereinbarte Pauschale." Eine Menge groesser eins waere die
       * Abrechnung nach Koepfen, die E-49 ausschliesst — der Server weist
       * sie ab, und die Maske bietet sie gar nicht erst an.
       */
      ...(braucthBetrag ? { menge: pauschal ? 1 : Number(menge) } : {}),
      ...(preis === '' ? {} : { einzelpreis: preis }),
      ...(art === 'rabatt' ? { steuerschluessel: steuersatz, steuerProzent: null } : {}),
      ...(rabatt === '' ? {} : { rabattProzent: rabatt }),
      /*
       * ANG-30: Mit den Wunschterminen entsteht die Anfrage an die
       * Disposition — in EINEM Vorgang mit der Zeile. Ohne Wunschtermin
       * bleibt es bei der Zeile; nicht jede Position braucht einen Tag.
       */
      ...(wunschtermine.length === 0
        ? {}
        : {
            terminoption: {
              teilnehmerzahl: Number(teilnehmer),
              standort: optionsort === '' ? null : optionsort,
              gewuenschteFristBis: optionsfrist === '' ? null : optionsfrist,
              wunschtermine,
            },
          }),
    });
    setzeArtikel(undefined);
    setzeForm(undefined);
    setzeVon('');
    setzeVonZeit(ERSTE_RASTERZEIT);
    setzeOffenerTermin('');
    setzeAlternativen([]);
    setzeTeilnehmer('');
    setzeOptionsfrist('');
    setzeBezeichnung('');
    setzeEinzelpreis('');
    setzeRabatt('');
    setzeMenge('1');
  }

  /*
   * ANG-02. Ein eigener Abschnitt unter der Tabelle (CRM-218): Als flache
   * Karte stand das Formular nur durch eine Linie von den Positionen
   * getrennt. Der Messpunkt der Kanalzeile bleibt (`karte--messend`).
   */
  return (
    <Abschnitt titel="Position hinzufügen">
      <div className="karte--messend">
        <div className="kanalzeile">
          <label className="feld__beschriftung" htmlFor="neu-art">
            Art
          </label>
          <select
            id="neu-art"
            value={art}
            onChange={(e) => {
              setzeArt(e.target.value as Positionsart);
              setzeWertung('wirksam');
            }}
          >
            {/*
              Die Rabattzeile steht seit AA-007 zur Wahl: PRF-11 nimmt sie von
              der Kappung auf null aus, und sie traegt ihren Abzug als echten
              negativen Betrag.
            */}
            {(['artikel', 'freitext', 'rabatt', 'text', 'zwischensumme'] as const).map((a) => (
              <option key={a} value={a}>
                {ARTNAME[a]}
              </option>
            ))}
          </select>
          {braucthBetrag ? (
            <select
              value={wertung}
              onChange={(e) => setzeWertung(e.target.value as Wertung)}
              aria-label="Wertung"
            >
              {(['wirksam', 'alternativ', 'optional'] as const).map((w) => (
                <option key={w} value={w}>
                  {WERTUNGSNAME[w]}
                </option>
              ))}
            </select>
          ) : (
            <span className="feld__hilfe">zählt nie in die Summe</span>
          )}
          <span />
        </div>

        {art === 'artikel' && (
          <>
            {/*
              DIE AUSWAHL LIEGT IN EINEM FENSTER, NICHT UNTER DEM FELD.
              Eine aufklappende Liste schob Menge, Preis und Steuersatz nach
              unten und zeigte nur acht Zeilen ohne Vertriebsform. Das Fenster
              ist dem allgemeinen Suchfeld nachgebaut — wer eines kennt, kennt
              beide.
            */}
            <div className="kanalzeile">
              <span className="feld__beschriftung" id="neu-artikel-beschriftung">
                Artikel
              </span>
              {artikel === undefined ? (
                <button
                  type="button"
                  className="knopf"
                  aria-describedby="neu-artikel-beschriftung"
                  onClick={() => setzeWahlOffen(true)}
                >
                  Artikel wählen …
                </button>
              ) : (
                <span className="kanalliste__wert">
                  {artikel.artikelnummer} · {artikel.bezeichnung}
                </span>
              )}
              {artikel === undefined ? (
                <span className="feld__hilfe">Nummer oder Bezeichnung</span>
              ) : (
                <button
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => setzeWahlOffen(true)}
                >
                  Anderen wählen
                </button>
              )}
              <span />
            </div>

            {wahlOffen && (
              <Artikelwahl
                beiSchliessen={() => setzeWahlOffen(false)}
                beiWahl={(gewaehlt) => {
                  setzeArtikel(gewaehlt);
                  setzeForm(undefined);
                  setzeWahlOffen(false);
                }}
              />
            )}

            {/*
              PRD-42: DIE WAHL ERSCHEINT NUR, WO ES ETWAS ZU WAEHLEN GIBT.
              Ein Produkt mit genau einer Vertriebsform bekommt kein Feld — die
              Auswahl mit einem Eintrag ist keine Auswahl, sondern eine Frage,
              die sich selbst beantwortet.
            */}
            {artikel !== undefined && zurWahl && (
              <div className="kanalzeile">
                <label className="feld__beschriftung" htmlFor="neu-form">
                  Verkaufsform
                </label>
                <select
                  id="neu-form"
                  value={gewaehlteForm ?? ''}
                  onChange={(e) => setzeForm(e.target.value as Vertriebsform)}
                >
                  {formen.map((f) => (
                    <option key={f} value={f}>
                      {VERTRIEBSFORMNAME[f]}
                    </option>
                  ))}
                </select>
                <span className="feld__hilfe">jede mit eigenem Preis</span>
                <span />
              </div>
            )}

            {/*
              DER OFFENE TERMIN — nur beim EINZELPLATZ (BUC-04, KAP-04).

              Auftraggeber, 16.09.2026: „Beim Artikel Fahrtraining und Auswahl
              einzelplatz soll es die Möglichkeit geben, keinen Termin
              anzugeben, oder einen offenen Termin für dieses Produkt
              auszuwählen, mit Anzeige der freien Plätze."

              „Frei" ist die maßgebliche Zahl und nicht die Höchstzahl: Sie
              rechnet Buchungen, Haltungen und Kontingente gegen (KAP-04) und
              kennt den Anfragenden — ein Kunde mit eigenem Kontingent sähe
              sonst „voll", obwohl Plätze für ihn zurückliegen (KAP-22).
            */}
            {artikel !== undefined && einzelplatz && (
              <>
                <div className="kanalzeile">
                  <label className="feld__beschriftung" htmlFor="neu-offener-termin">
                    Termin
                  </label>
                  <select
                    id="neu-offener-termin"
                    value={offenerTermin}
                    onChange={(e) => setzeOffenerTermin(e.target.value)}
                  >
                    <option value="">— ohne Termin</option>
                    {offeneTermine.map((o) => (
                      <option key={o.id} value={o.id}>
                        {`${zeitpunkt(o.beginn)} Uhr · ${String(o.frei)} von `
                          + `${String(o.hoechstteilnehmer)} frei`}
                      </option>
                    ))}
                  </select>
                  <span className="feld__hilfe">
                    {terminfehler
                      ? 'Auskunft nicht erreichbar'
                      : offeneTermine.length === 0
                        ? 'kein buchbarer Termin'
                        : `${String(offeneTermine.length)} buchbar`}
                  </span>
                  <span />
                </div>
                <p className="feld__hilfe">
                  {terminfehler
                    ? 'Die Terminauskunft antwortet nicht. Ob es buchbare Termine gibt, lässt '
                      + 'sich gerade nicht sagen — legen Sie die Position ohne Termin an und '
                      + 'tragen Sie ihn nach.'
                    : offenerTermin === ''
                      ? 'Ohne Termin ist es ein Preisangebot: Die Zeile nennt Leistung und '
                        + 'Betrag, der Platz wird erst mit der Buchung gehalten.'
                      : 'Der Termin bringt seinen Standort in die Preisfindung mit. Plätze '
                        + 'hält das Angebot dadurch noch nicht — das tut erst die Buchung.'}
                </p>
              </>
            )}

            {/*
              ANG-25: der Gegenstand der Position — beim Exklusivverkauf ein
              WUNSCHDATUM und kein bestehender Termin.

              ============================================================
              HIER STAND EIN TERMINWAEHLER, UND ER IST AM 15.09.2026 WEG
              ============================================================

              Er beantwortete die Frage, ob die Zeile einen Termin meint, den es
              schon gibt. Auftraggeber: „ich bin mir nicht sicher, ob wir das
              brauchen." Nachgesehen — er brauchte ihn nicht:

                * Ein Exklusivtermin ENTSTEHT aus dem Angebot (BUC-68), über den
                  Wunschtermin oder über die Terminoption, die die Disposition
                  beantwortet (ANG-30, ANG-39). Keiner dieser Wege ging über den
                  Wähler.
                * Im gesamten Bestand stand keine einzige der 150
                  Exklusivpositionen auf einem gewählten Termin — alle auf
                  „zeitraum".
                * Und was er anrichten konnte, fing nichts ab: Ein zweites
                  Angebot auf einen bereits verkauften Termin wäre ein zweiter
                  Auftrag über denselben Tag, dessen Kapazität nach BUC-68 EINMAL
                  und ganz vergeben ist. Weder Index noch Zusatz verhinderten es.

              Was bleibt, ist der Regelfall: das Wunschdatum. Der Preis kommt aus
              der Liste, und mit der Annahme wird der angenommene Betrag zur
              Exklusivvereinbarung (ANG-27, DAT-08) — pflegbar danach am Termin.
            */}
            {artikel !== undefined && gewaehlteForm === 'exklusivtermin' && (
              <>
                {/*
                  ANG-25: „jeweils mit ZEITRAUM und Ressourcenbezug".

                  Beim Angebot ist der Termin meist noch nicht angelegt — nach
                  BUC-68 entsteht er erst aus dem angenommenen Angebot. Was der
                  Kunde zugesagt bekommt, ist ein DATUM, und genau das fehlte:
                  Die Spalten stehen seit Migration 0108 da, die Schnittstelle
                  nimmt sie an, und die Maske hatte kein Feld dafür. Wer einen
                  Exklusivtag anbot, konnte nicht sagen, wann.

                  Das Datum geht als Bezugsart „zeitraum" hinaus — die einzige,
                  die `angebotsposition_bezugsart_belegt` zusammen mit bezugVon
                  zulässt, und seit dem 15.09.2026 die einzige, die hier
                  überhaupt entsteht.
                */}
                <div className="kanalzeile">
                  <label className="feld__beschriftung" htmlFor="neu-von">
                    Wunschtermin
                  </label>
                  <input
                    id="neu-von"
                    type="date"
                    value={von}
                    onChange={(e) => setzeVon(e.target.value)}
                    aria-describedby="hinweis-wunschtermin"
                  />
                  {/*
                    DIE UHRZEIT (Auftraggeber, 13.09.2026: „kunden haben
                    häufig feste vorstellungen"). Zur Auswahl stehen die
                    Anfangszeiten des Rasters (ÄA-26): Aus dem Wunsch wird
                    bei der Annahme ein Termin, und der beginnt alle 45
                    Minuten ab acht.

                    SEIT DEM 16.09.2026 GIBT ES KEINEN LEEREN EINTRAG MEHR.
                    „Tagesbeginn" hieß: keine Uhrzeit mitschicken, der Server
                    nimmt die des Standorts. Dieselbe Zeit — aber sie stand dann
                    nirgends, und wer das Angebot prüfte, sah einen Wunsch ohne
                    Uhrzeit. Vorbelegt ist jetzt 08:00.
                  */}
                  <select
                    value={vonZeit}
                    onChange={(e) => setzeVonZeit(e.target.value)}
                    aria-label="Uhrzeit des Wunschtermins"
                  >
                    {RASTERZEITEN.map((z) => (
                      <option key={z} value={z}>
                        {z}
                      </option>
                    ))}
                  </select>
                  <span className="feld__hilfe">Anfangszeiten alle 45 Minuten ab 8 Uhr</span>
                </div>

                {alternativen.map((a, nr) => (
                  /* Die Reihenfolge IST die Kennung der Alternative (ANG-31). */
                  <div className="kanalzeile" key={nr}>
                    <label className="feld__beschriftung" htmlFor={`neu-alt-${String(nr)}`}>
                      {nr + 1}. Alternative
                    </label>
                    <input
                      id={`neu-alt-${String(nr)}`}
                      type="date"
                      value={a.tag}
                      onChange={(e) => {
                        const wert = e.target.value;
                        setzeAlternativen((alt) =>
                          alt.map((x, i) => (i === nr ? { ...x, tag: wert } : x)),
                        );
                      }}
                    />
                    <select
                      value={a.zeit}
                      aria-label={`Uhrzeit der ${String(nr + 1)}. Alternative`}
                      onChange={(e) => {
                        const wert = e.target.value;
                        setzeAlternativen((alt) =>
                          alt.map((x, i) => (i === nr ? { ...x, zeit: wert } : x)),
                        );
                      }}
                    >
                        {RASTERZEITEN.map((z) => (
                        <option key={z} value={z}>
                          {z}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="knopf knopf--eingriff knopf--klein"
                      onClick={() => {
                        setzeAlternativen((alt) => alt.filter((_, i) => i !== nr));
                      }}
                    >
                      Entfernen
                    </button>
                  </div>
                ))}

                {von !== '' && alternativen.length < 2 && (
                  <div className="kanalzeile">
                    <span />
                    <button
                      type="button"
                      className="knopf knopf--klein"
                      onClick={() => {
                        setzeAlternativen((alt) => [...alt, { tag: '', zeit: ERSTE_RASTERZEIT }]);
                      }}
                    >
                      Alternativtermin hinzufügen
                    </button>
                    <span className="feld__hilfe">höchstens drei Termine je Anfrage</span>
                    <span />
                  </div>
                )}

                {von !== '' && (
                  <>
                    <div className="kanalzeile">
                      <label className="feld__beschriftung" htmlFor="neu-teilnehmer">
                        Teilnehmer
                      </label>
                      <input
                        id="neu-teilnehmer"
                        type="number"
                        min={1}
                        value={teilnehmer}
                        onChange={(e) => setzeTeilnehmer(e.target.value)}
                      />
                      <span className="feld__hilfe">so viele Plätze hält die Disposition</span>
                      <span />
                    </div>
                    <div className="kanalzeile">
                      <label className="feld__beschriftung" htmlFor="neu-optionsort">
                        Standort
                      </label>
                      <select
                        id="neu-optionsort"
                        value={optionsort}
                        onChange={(e) => setzeOptionsort(e.target.value)}
                      >
                        {/* Leer heißt: der Hauptstandort — das entscheidet der Server. */}
                        <option value="">— Hauptstandort —</option>
                        {orte.map((o) => (
                          <option key={o.kennung} value={o.kennung}>
                            {o.bezeichnung}
                          </option>
                        ))}
                      </select>
                      <span className="feld__hilfe">bestimmt Tagesrahmen und Ressourcen</span>
                      <span />
                    </div>
                    <div className="kanalzeile">
                      <label className="feld__beschriftung" htmlFor="neu-optionsfrist">
                        gehalten bis
                      </label>
                      <input
                        id="neu-optionsfrist"
                        type="date"
                        value={optionsfrist}
                        onChange={(e) => setzeOptionsfrist(e.target.value)}
                      />
                      <span className="feld__hilfe">
                        gewünschte Optionsfrist — die Disposition setzt sie
                      </span>
                      <span />
                    </div>
                      </>
                    )}

                    {/*
                      HIER STAND EIN FELD „BIS", UND ES IST AM 16.09.2026 WEG.

                      Auftraggeber: „Bei der Auswahl exklusivtermin soll es keine
                      Option ‚bis' geben, da Fahrtrainings nur einen Tag sind und
                      ansonsten alternativtermine angegeben werden."

                      Es war der Endtag eines Zeitraums. Ein Exklusivtraining
                      dauert einen Tag; wer zwei Tage wollte, meinte zwei
                      Termine — und die stehen als Alternativen darüber. Die
                      Spalte `bezugBis` bleibt: Eine Ressourcenüberlassung wird
                      sehr wohl über einen Zeitraum verkauft (ANG-25), nur nicht
                      hier.
                    */}
                    <p className="feld__hilfe" id="hinweis-wunschtermin">
                      {von === ''
                        ? 'Ohne Wunschtermin entsteht nur die Zeile mit ihrem Preis — kein '
                          + 'Termin und keine Anfrage an die Disposition. Das ist der Weg '
                          + 'für ein schnelles Angebot; disponiert wird nach der Annahme.'
                        : 'Mit dem Anlegen geht eine Anfrage an die Disposition, und jeder '
                          + 'Wunschtermin steht als Option im Kalender. Bis zur Zusage trägt '
                          + 'die Zeile auf dem Dokument „Termin auf Anfrage".'}
                    </p>
              </>
            )}
          </>
        )}

        {art !== 'artikel' && (
          <div className="kanalzeile">
            <label className="feld__beschriftung" htmlFor="neu-bezeichnung">
              {art === 'rabatt' ? 'Bezeichnung des Nachlasses' : 'Text'}
            </label>
            <input
              id="neu-bezeichnung"
              type="text"
              value={bezeichnung}
              onChange={(e) => setzeBezeichnung(e.target.value)}
            />
            <span />
            <span />
          </div>
        )}

        {braucthBetrag && (
          <div className="kanalzeile">
            <label className="feld__beschriftung" htmlFor="neu-menge">
              Menge
            </label>
            <input
              id="neu-menge"
              type="text"
              inputMode="numeric"
              value={pauschal ? '1' : menge}
              disabled={pauschal}
              aria-describedby={pauschal ? 'hinweis-pauschale' : undefined}
              onChange={(e) => setzeMenge(e.target.value)}
            />
            <input
              type="text"
              value={einzelpreis}
              onChange={(e) => setzeEinzelpreis(e.target.value)}
              /*
               * DER GEFUNDENE PREIS STEHT IM FELD — als Platzhalter, nicht als
               * Wert. Ein vorbelegter Wert ginge als Uebersteuerung hinaus
               * (ANG-05 vermerkt sie an der Position), und jede Zeile traege
               * dann den Vermerk „von Hand gesetzt", auch wenn niemand etwas
               * gesetzt hat. Leer heisst: nimm den Katalogpreis.
               */
              placeholder={
                art === 'artikel'
                  ? (alsTippbar(auskunft?.preis?.einzelpreis) ?? 'Preis aus dem Katalog')
                  : art === 'rabatt'
                    ? 'Abzug, z. B. 150.00'
                    : 'Einzelpreis'
              }
              aria-label={art === 'rabatt' ? 'Abzug' : 'Einzelpreis'}
            />
            {art === 'rabatt' ? (
              <select
                value={steuersatz}
                onChange={(e) => setzeSteuersatz(e.target.value)}
                aria-label="Steuerschlüssel des Abzugs"
              >
                {SATZWAHL.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={rabatt}
                onChange={(e) => setzeRabatt(e.target.value)}
                placeholder="Rabatt %"
                aria-label="Positionsrabatt in Prozent"
              />
            )}
          </div>
        )}

        {/*
          DER PREIS, BEVOR DIE ZEILE ENTSTEHT (ANG-05, PRF-01 bis PRF-16).

          Auftraggeber, 16.09.2026: „Der Artikelpreis soll angezeigt werden, mit
          der Option ihn zu überschreiben, bzw. einen Rabatt in Prozent
          anzugeben."

          Gezeigt wird, was die Preisfindung ANTWORTET — dieselbe Kette, die
          gleich die Zeile rechnet. Steht ein Handpreis im Feld, tritt er an die
          Stelle des gefundenen; steht ein Rabatt daneben, mindert er ihn. Beides
          rechnet die Vorschau mit, damit unten steht, was der Kunde liest.
        */}
        {art === 'artikel' && artikel !== undefined && (
          <div className="preisvorschau">
            {preisLaeuft && auskunft === undefined ? (
              <p className="feld__hilfe" role="status">Preis wird ermittelt …</p>
            ) : auskunft?.preis == null ? (
              <p className="feld__hilfe">
                {auskunft === undefined
                  ? 'Der Preis lässt sich gerade nicht ermitteln. Die Position lässt sich '
                    + 'trotzdem anlegen — dann rechnet der Beleg ihn.'
                  : 'Für diese Verkaufsform ist kein Preis gepflegt. Tragen Sie einen Betrag '
                    + 'ein, sonst weist der Server die Position ab.'}
              </p>
            ) : (
              <dl className="angaben angaben--eng">
                {/*
                  „TERMINPREIS" UND NICHT „PAUSCHALE".
                  Eine Pauschale ist eine VEREINBARUNG am Termin (DAT-08). Ohne
                  Termin steht hier der Listenpreis fuer den ganzen Tag — ihn
                  „Pauschale" zu nennen behauptete eine Zusage, die es nicht gibt.
                */}
                <dt>{pauschal ? 'Terminpreis' : 'Einzelpreis'}</dt>
                <dd>
                  <strong>{alsEuro(auskunft.preis.einzelpreis)}</strong>
                  {auskunft.preis.istBrutto ? ' brutto' : ' netto'}
                  {auskunft.preis.staffelpreis !== null && auskunft.preis.abMenge !== null
                    && ` · Staffel ab ${String(auskunft.preis.abMenge)}`}
                </dd>
                <dt>Herkunft</dt>
                <dd>{auskunft.preis.preisliste}</dd>
                {auskunft.preis.hoechstrabattProzent !== null && (
                  <>
                    <dt>Höchstrabatt</dt>
                    <dd>{alsProzent(auskunft.preis.hoechstrabattProzent)}</dd>
                  </>
                )}
                <dt>Zeile</dt>
                <dd>
                  {pauschal ? '1 Termin' : `${menge} × ${alsEuro(auskunft.preis.einzelpreis)}`}
                  {' = '}
                  {alsEuro(auskunft.preis.netto)} netto
                  {' · '}
                  <strong>{alsEuro(auskunft.preis.bruttoMitSteuer)}</strong> brutto
                </dd>
              </dl>
            )}
            <p className="feld__hilfe">
              Ein Betrag im Preisfeld übersteuert ihn; die Übersteuerung wird an der Position
              vermerkt. Ein Rabatt in Prozent mindert den gefundenen Preis, ohne ihn zu ersetzen —
              er steht damit auf dem Dokument als Nachlass.
            </p>
          </div>
        )}

        {art === 'rabatt' && (
          <p className="feld__hilfe">
            Der Abzug wird mit Minus gebucht — Sie tippen den Betrag ohne Vorzeichen. Der
            Steuerschlüssel bestimmt, welche Bemessungsgrundlage er mindert: Die Steuer wird je
            Satzgruppe gerechnet, und ein Abzug ohne Gruppe gehört in keine.
          </p>
        )}

        {/*
          EIN GESPERRTES FELD MUSS SAGEN, WARUM.

          Die Menge steht beim Exklusivverkauf auf eins und laesst sich nicht
          aendern (PRD-46, E-49). Bis zum 15.09.2026 stand die Begruendung im
          Absatz am Terminwaehler; mit ihm waere sie verschwunden, und
          zurueckgeblieben waere ein graues Feld ohne Grund. Der Hinweis gehoert
          ohnehin ans Feld — deshalb zeigt `aria-describedby` der Menge hierher.
        */}
        {pauschal && (
          <p className="feld__hilfe" id="hinweis-pauschale">
            Die Menge ist eins: Berechnet wird der <strong>Termin</strong>, nicht der Platz. Der
            Betrag hängt am vereinbarten Umfang und nicht an der Teilnehmerzahl — eine Abrechnung
            nach Köpfen ist bei Exklusivterminen nicht vorgesehen. Wie viele Plätze der Kunde
            bekommt, sagen Sie beim Wunschtermin unter „Teilnehmer".
          </p>
        )}

        <button type="button" className="knopf knopf--haupt" disabled={!bereit} onClick={anlegen}>
          Hinzufügen
        </button>
      </div>
    </Abschnitt>
  );
}

/** Die Kopfdaten (ANG-01). Feldweises Sichern wie in der Kundenakte. */
function Kopfdaten({
  angebot,
  zurKunde,
  bearbeitbar,
  beiSichern,
}: {
  readonly angebot: Angebot;
  /** CRM-181: der Weg zur Kundenakte, nur mit `kunde:sehen` — zugleich das Recht der Kontaktkarte. */
  readonly zurKunde: ((id: string) => void) | undefined;
  readonly bearbeitbar: boolean;
  readonly beiSichern: (feld: string, wert: string | null) => void;
}): JSX.Element {
  return (
    <div className="tafel__abschnitte">
      {/* ANG-01 */}
      <Abschnitt titel="Kopf">
        {/*
          Zwei Untergruppen (CRM-218): Was feststeht, und was auf dem Beleg
          steht und sich hier aendern laesst. Bis dahin liefen die Angaben
          ohne Grenze in die Felder.
        */}
        <Feldgruppe titel="Beteiligte und Vorgaben">
          <dl className="angaben">
            <dt>Kunde</dt>
            <dd>
              <KundeVerweis kundeId={angebot.kunde} zurAkte={zurKunde}>
                {angebot.kundenname}
              </KundeVerweis>
            </dd>
            <dt>Ansprechpartner</dt>
            <dd>
              {angebot.ansprechpartnerName === null ? (
                '— keiner'
              ) : (
                <KundeVerweis kundeId={angebot.kunde} ansprechpartnerId={angebot.ansprechpartner} zurAkte={zurKunde}>
                  {angebot.ansprechpartnerName}
                </KundeVerweis>
              )}
            </dd>
            <dt>Bearbeiter</dt>
            <dd>{angebot.bearbeiterName ?? '—'}</dd>
            <dt>Belegdatum</dt>
            <dd>{angebot.belegdatum}</dd>
            <dt>Preisführung</dt>
            <dd>
              {angebot.preisfuehrung === 'brutto' ? 'brutto' : 'netto'}
              {/* ANG-13 */}
              <span className="kanalliste__name">
                {' '}
                · vorbelegt nach Kundenart {angebot.kundenart}
              </span>
            </dd>
            {/*
              ANG-43: "Jedes Angebot benennt AUSDRUECKLICH die Vertragsart [...]
              Sie steht auf dem Angebotsdokument." Sie gehoert damit auch in die
              Maske — und nicht nur, weil das Lastenheft es sagt: Sie bestimmt
              nach BUC-45, was ein Ruecktritt kostet.
            */}
            <dt>Vertragsart</dt>
            <dd>
              {VERTRAGSARTNAME[angebot.vertragsart]}
              <span className="kanalliste__name"> · bestimmt die Stornostaffel</span>
            </dd>
            {/*
              PAR-03: Das Vermittlerkennzeichen steht in der Akte, nicht nur in
              der Datenbank. Wer den Beleg ansieht, muss sehen, wem die
              Vermittlung zugerechnet wird — sie entscheidet über die Auswertung
              nach PAR-01 und ist nachträglich nur mit Grund änderbar.
            */}
            {angebot.vermittler !== null && (
              <>
                <dt>Vermittler</dt>
                <dd>
                  {angebot.vermittlername ?? '—'}
                  {angebot.vermittlercode !== null && (
                    <span className="kanalliste__name"> · {angebot.vermittlercode}</span>
                  )}
                </dd>
              </>
            )}
          </dl>
        </Feldgruppe>
        <Feldgruppe titel="Auf dem Beleg">
          <Feld
            name="betreff"
            beschriftung="Betreff"
            wert={angebot.betreff}
            bearbeitbar={bearbeitbar}
            beiSichern={beiSichern}
          />
          <Feld
            name="gueltigBis"
            beschriftung="Gültig bis"
            wert={angebot.gueltigBis}
            bearbeitbar={bearbeitbar}
            beiSichern={beiSichern}
          />
          <Feld
            name="kundenreferenz"
            beschriftung="Referenz des Kunden"
            wert={angebot.kundenreferenz ?? ''}
            bearbeitbar={bearbeitbar}
            beiSichern={beiSichern}
          />
        </Feldgruppe>
      </Abschnitt>

      <Abschnitt titel="Texte">
        <Feld
          name="kopftext"
          beschriftung="Kopftext"
          wert={angebot.kopftext ?? ''}
          mehrzeilig
          bearbeitbar={bearbeitbar}
          beiSichern={beiSichern}
        />
        <Feld
          name="fusstext"
          beschriftung="Fußtext"
          wert={angebot.fusstext ?? ''}
          mehrzeilig
          bearbeitbar={bearbeitbar}
          beiSichern={beiSichern}
        />
      </Abschnitt>
    </div>
  );
}

/** Ein Feld, das beim Verlassen sichert — dasselbe Muster wie in der Kundenakte. */
function Feld({
  name,
  beschriftung,
  wert,
  mehrzeilig,
  bearbeitbar,
  beiSichern,
}: {
  readonly name: string;
  readonly beschriftung: string;
  readonly wert: string;
  readonly mehrzeilig?: boolean;
  readonly bearbeitbar: boolean;
  readonly beiSichern: (feld: string, wert: string | null) => void;
}): JSX.Element {
  const [entwurf, setzeEntwurf] = useState(wert);
  useEffect(() => setzeEntwurf(wert), [wert]);

  const sichern = (): void => {
    if (entwurf === wert) return;
    beiSichern(name, entwurf === '' ? null : entwurf);
  };

  return (
    <div className="feld">
      <label className="feld__beschriftung" htmlFor={`feld-${name}`}>
        {beschriftung}
      </label>
      {mehrzeilig === true ? (
        <textarea
          id={`feld-${name}`}
          value={entwurf}
          rows={4}
          disabled={!bearbeitbar}
          onChange={(e) => setzeEntwurf(e.target.value)}
          onBlur={sichern}
        />
      ) : (
        <input
          id={`feld-${name}`}
          type="text"
          value={entwurf}
          disabled={!bearbeitbar}
          onChange={(e) => setzeEntwurf(e.target.value)}
          onBlur={sichern}
        />
      )}
    </div>
  );
}

/**
 * Der Summenblock (ANG-06).
 *
 * JE STEUERSATZGRUPPE und nicht je Position: RND-06 verlangt genau diese
 * Rechnung, und der Validator der E-Rechnung nach RNG-14 prueft sie nach.
 */
function Summen({ angebot }: { readonly angebot: Angebot }): JSX.Element {
  const s = angebot.summen;
  return (
    <section className="seitenblock" aria-labelledby="angebot-summen">
      <h2 id="angebot-summen" className="seitenblock__titel">
        Summen
      </h2>
      {/* ANG-06, RND-05, RND-07: je Steuersatzgruppe, nicht je Position. */}
      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle summentafel">
          <thead>
            <tr>
              <th scope="col">Steuersatz</th>
              <th scope="col" className="zahl">
                Netto
              </th>
              <th scope="col" className="zahl">
                Steuer
              </th>
              <th scope="col" className="zahl">
                Brutto
              </th>
            </tr>
          </thead>
          <tbody>
            {s.gruppen.map((g) => (
              <tr key={g.schluessel}>
                <td>
                  {g.schluessel} · {alsProzent(g.prozent)}
                  {g.hinweis !== null && <span className="kanalliste__name"> · {g.hinweis}</span>}
                </td>
                <td className="zahl">{alsEuro(g.netto)}</td>
                <td className="zahl">{alsEuro(g.steuer)}</td>
                <td className="zahl">{alsEuro(g.brutto)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">Gesamt</th>
              <td className="zahl">{alsEuro(s.belegnetto)}</td>
              <td className="zahl">{alsEuro(s.steuerSumme)}</td>
              <td className="zahl">
                <strong>{alsEuro(s.belegbrutto)}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {s.gruppen.length === 0 && <p className="feld__hilfe">Noch keine berechnete Position.</p>}

      <Rundungshinweis faelle={s.nichtDarstellbar} />

      {/* ANG-03 */}
      <dl className="angaben" aria-label="Nicht gewertet">
        <dt>Nicht gewertet</dt>
        <dd>{alsEuro(s.nichtGewertet)}</dd>
      </dl>
      <p className="feld__hilfe">
        Optionale und alternative Positionen fließen nicht in die Angebotssumme ein, stehen aber
        auf dem Dokument. Dieser Betrag sagt, worum es dabei geht.
      </p>
    </section>
  );
}

/**
 * DIE BELEGKETTE DES ANGEBOTS (CRM-118, L7; AUF-06): der Kunde, der Auftrag,
 * der daraus entstand, und die Termine, auf die seine Positionen zeigen.
 */
function Angebotsverbunden({
  angebot,
  wege,
}: {
  readonly angebot: Angebot;
  readonly wege: Aktenwege | undefined;
}): JSX.Element {
  const termine = angebot.positionen.filter(
    (p) => p.bezugsart === 'termin' && p.bezugId !== null && p.bezugId !== '',
  );
  return (
    <Verbunden
      zeilen={[
        {
          beschriftung: 'Kunde',
          eintraege: [
            <Aktenverweis key="k" ziel="kunde" id={angebot.kunde} wege={wege}>
              {angebot.kundenname}
            </Aktenverweis>,
          ],
        },
        {
          beschriftung: 'Auftrag',
          eintraege:
            angebot.auftrag === null
              ? []
              : [
                  <Aktenverweis key="a" ziel="auftrag" id={angebot.auftrag.id} wege={wege}>
                    {angebot.auftrag.auftragsnummer}
                  </Aktenverweis>,
                ],
        },
        {
          beschriftung: 'Termine',
          eintraege: termine.map((p) => (
            <Aktenverweis key={p.id} ziel="termin" id={p.bezugId} wege={wege}>
              {p.bezeichnung}
            </Aktenverweis>
          )),
        },
      ]}
    />
  );
}
